import React, { useState, useEffect, useRef } from 'react';
import { notify, confirmDialog } from './ui/notify';
import { apiCall } from './config';
import { useTranslation, LANGUAGES } from './i18n/LanguageContext';

// Dokument-Vorschau (Ausweis, Arbeitserlaubnis, Aufenthaltstitel):
// Bilder als klickbares Thumbnail → Klick öffnet FULLSCREEN-Ansicht.
// PDFs als klickbare Kachel → Klick öffnet das PDF fullscreen (als Blob-URL,
// weil Chrome data:-PDFs weder im Tab noch zuverlässig im iframe anzeigt).
// Dokument-Ansicht mit Vollbild. Im Vollbild: Klick ins Bild zoomt auf 2,5×
// (an der geklickten Stelle), nochmal Klick zoomt zurück – z.B. um eine
// Ausweisnummer lesen zu können. Gezoomt lässt sich das Bild scrollen.
//   mime      – MIME-Typ, falls src eine blob:/http-URL ist (sonst aus data: erkannt)
//   autoOpen  – direkt im Vollbild starten (ohne Vorschaubild)
//   hideThumb – kein Vorschaubild rendern (nur das Vollbild)
//   onClose   – Rückruf beim Schließen
export function DocView({ src, alt = 'Dokument', maxHeight = 200, mime, autoOpen = false, hideThumb = false, onClose }) {
  const [open, setOpen] = useState(!!autoOpen);
  const [blobUrl, setBlobUrl] = useState(null);
  const [zoom, setZoom] = useState(false);
  const [zoomAt, setZoomAt] = useState({ x: 0.5, y: 0.5 });
  const scrollBox = useRef(null);
  const isDataPdf = typeof src === 'string' && src.startsWith('data:application/pdf');
  const isPdf = isDataPdf || mime === 'application/pdf';

  const close = () => { setOpen(false); setZoom(false); if (onClose) onClose(); };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !isPdf) return undefined;
    if (!isDataPdf) { setBlobUrl(src); return undefined; }   // blob:/http-URL direkt verwenden
    try {
      const b64 = src.split(',')[1];
      const bytes = atob(b64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([arr], { type: 'application/pdf' }));
      setBlobUrl(url);
      return () => { URL.revokeObjectURL(url); setBlobUrl(null); };
    } catch (_) { return undefined; }
  }, [open, isPdf, isDataPdf, src]);

  // Nach dem Zoomen die geklickte Stelle in die Mitte des sichtbaren Bereichs holen
  useEffect(() => {
    const box = scrollBox.current;
    if (!zoom || !box) return;
    box.scrollLeft = zoomAt.x * box.scrollWidth - box.clientWidth / 2;
    box.scrollTop = zoomAt.y * box.scrollHeight - box.clientHeight / 2;
  }, [zoom, zoomAt]);

  if (!src) return null;

  const onImgClick = (e) => {
    e.stopPropagation();
    if (zoom) { setZoom(false); return; }
    const r = e.currentTarget.getBoundingClientRect();
    setZoomAt({ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height });
    setZoom(true);
  };

  const overlay = open ? (
    <div
      onClick={close}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.88)', zIndex: 3000,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 10 }}
    >
      <div className="d-flex align-items-center gap-3 mb-2" onClick={(e) => e.stopPropagation()}>
        <span className="text-white fw-bold">{alt}</span>
        {!isPdf && (
          <span className="text-white-50 small">{zoom ? '🔍 Klick = zurück' : '🔍 Klick ins Bild = zoomen'}</span>
        )}
        {isPdf && blobUrl && (
          <a href={blobUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-light">
            📄 In neuem Tab öffnen
          </a>
        )}
        <button type="button" className="btn btn-sm btn-outline-light" onClick={close}>
          ✕ Schließen (Esc)
        </button>
      </div>
      {isPdf ? (
        blobUrl
          ? <iframe src={blobUrl} title={alt} onClick={(e) => e.stopPropagation()}
              style={{ width: '96vw', height: '88vh', border: 0, borderRadius: 8, background: '#fff' }} />
          : <div className="text-white">PDF wird geladen…</div>
      ) : (
        <div ref={scrollBox} onClick={(e) => e.stopPropagation()}
          style={{ width: '96vw', height: '88vh', overflow: zoom ? 'auto' : 'hidden',
            display: 'flex', alignItems: zoom ? 'flex-start' : 'center', justifyContent: zoom ? 'flex-start' : 'center' }}>
          <img src={src} alt={alt} onClick={onImgClick}
            style={zoom
              ? { width: '250%', maxWidth: 'none', height: 'auto', cursor: 'zoom-out', background: '#fff', flexShrink: 0 }
              : { maxWidth: '96vw', maxHeight: '88vh', objectFit: 'contain', borderRadius: 8, background: '#fff', cursor: 'zoom-in' }} />
        </div>
      )}
    </div>
  ) : null;

  if (hideThumb) return overlay;

  if (isPdf) {
    return (
      <>
        <div role="button" onClick={() => setOpen(true)}
          className="border rounded p-2 d-inline-flex align-items-center gap-2"
          style={{ cursor: 'zoom-in' }} title={`${alt} ansehen`}>
          <i className="bi bi-file-earmark-pdf text-danger fs-4"></i>
          <span>📄 {alt} (PDF) – <u>ansehen</u></span>
        </div>
        {overlay}
      </>
    );
  }
  return (
    <>
      <img src={src} alt={alt} className="img-thumbnail" title={`${alt} – klicken für Vollbild`}
        style={{ maxHeight, cursor: 'zoom-in' }} onClick={() => setOpen(true)} />
      {overlay}
    </>
  );
}

// Geburtsdatum für die Anzeige vereinheitlichen: "12062005" / "2005-06-12" /
// "12/06/2005" → "12.06.2005" (alles andere unverändert)
export function fmtBirthDE(v) {
  const s = String(v || '').trim().replace(/,/g, '.'); // "18,02,1968" → Punkte
  if (/^\d{8}$/.test(s)) return `${s.slice(0, 2)}.${s.slice(2, 4)}.${s.slice(4)}`;
  // "210109" (TTMMJJ) → 21.01.2009; JJ > aktuelles Jahr → 19xx
  if (/^\d{6}$/.test(s)) {
    const yy = Number(s.slice(4));
    const century = yy > (new Date().getFullYear() % 100) ? '19' : '20';
    return `${s.slice(0, 2)}.${s.slice(2, 4)}.${century}${s.slice(4)}`;
  }
  // "21.1.09" / "21.01.09" (zweistelliges Jahr mit Punkten)
  if (/^\d{1,2}\.\d{1,2}\.\d{2}$/.test(s)) {
    const [d, m, y] = s.split('.');
    const century = Number(y) > (new Date().getFullYear() % 100) ? '19' : '20';
    return `${d.padStart(2, '0')}.${m.padStart(2, '0')}.${century}${y}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}`;
  if (/^\d{2}[/-]\d{2}[/-]\d{4}$/.test(s)) return s.replace(/[/-]/g, '.');
  return s;
}

// Rückwärtskompatibler Helfer (bisherige Aufrufstellen)
function renderIdDoc(src, maxHeight = 200) {
  if (!src) return null;
  return <DocView src={src} alt="Ausweis" maxHeight={maxHeight} />;
}

const DigitalWorkContract = ({
  user, 
  isTemplate = false, 
  contractData = null, 
  templateData = null,
  onSave = null, 
  isEmployee = false,
  readonly = false,
  onPrintPDF = null // Neue Prop für PDF-Export
}) => {
  // EU-Länder Liste
  const euCountries = [
    'Deutschland',
    'Österreich',
    'Belgien',
    'Bulgarien',
    'Kroatien',
    'Zypern',
    'Tschechien',
    'Dänemark',
    'Estland',
    'Finnland',
    'Frankreich',
    'Griechenland',
    'Ungarn',
    'Irland',
    'Italien',
    'Lettland',
    'Litauen',
    'Luxemburg',
    'Malta',
    'Niederlande',
    'Polen',
    'Portugal',
    'Rumänien',
    'Slowakei',
    'Slowenien',
    'Spanien',
    'Schweden'
  ];

  // Nicht-EU-Länder (für Nationalitäts-Auswahl im Wizard; steuert Arbeitserlaubnis-Pflicht)
  const nonEuCountries = [
    'Albanien', 'Afghanistan', 'Ägypten', 'Algerien', 'Argentinien', 'Armenien', 'Australien',
    'Aserbaidschan', 'Bangladesch', 'Belarus', 'Bosnien und Herzegowina', 'Brasilien', 'China',
    'Georgien', 'Indien', 'Indonesien', 'Iran', 'Irak', 'Japan', 'Kanada', 'Kasachstan', 'Kenia',
    'Kolumbien', 'Kosovo', 'Libanon', 'Marokko', 'Mexiko', 'Moldau', 'Montenegro', 'Nepal',
    'Nigeria', 'Nordmazedonien', 'Norwegen', 'Pakistan', 'Peru', 'Philippinen', 'Russland',
    'Schweiz', 'Serbien', 'Südafrika', 'Südkorea', 'Sri Lanka', 'Syrien', 'Thailand', 'Tunesien',
    'Türkei', 'Ukraine', 'USA', 'Venezuela', 'Vietnam', 'Sonstige'
  ];

  const { t, language, setLanguage } = useTranslation();

  // PDF language: German for de/bar, English for all others
  const contractPdfLang = (language === 'de' || language === 'bar') ? 'de' : 'en';

  const [formData, setFormData] = useState({
    // Arbeitgeberdaten
    employerName: 'Bavarian Concept Group GmbH',
    employerAddress: 'Bürkelweg 18\n85540 Haar',
    
    // Arbeitnehmerdaten
    employeeName: '',
    employeeStreet: '',
    employeeHouseNumber: '',
    employeePLZ: '',
    employeeCity: '',
    employeeAddress: '', // auto-computed from street/nr/plz/city
    employeeBirthdate: '',
    employeeNationality: '',
    socialSecurityNumber: '',
    birthPlace: '',
    taxId: '',
    socialBenefitStatus: '', // 'grundsicherung', 'arbeitslosengeld', 'rente', 'keine' - leer = muss gewählt werden
    idCardImage: null, // Ausweisbild (Vorderseite) - Pflichtfeld
    iban: '',
    accountHolder: '', // Für abweichenden Kontoinhaber
    workPermitDocument: null, // Nachweis Arbeitserlaubnis
    residencePermitDocument: null, // Nachweis Aufenthaltstitel
    
    // Vertragsdaten
    position: 'Unterstützung bei der Gepäckaufbewahrung auf dem Oktoberfest in München',
    startDate: '19.09.2026',
    endDate: '04.10.2026',
    workingTime: 'durchschnittlich 8 Stunden pro Tag',
    salary: '15',
    salaryType: 'stundenlohn',
    
    // Arbeitszeit Details
    breakTime: 'Der Arbeitnehmer verpflichtet sich, die gesetzlichen Mindestpausen selbstständig einzuhalten.',
    
    // Besondere Vereinbarungen
    vacationDays: 'Der Arbeitnehmer hat aufgrund einer befristeten Tätigkeit von maximal 16 Tagen keinen Anspruch auf Urlaub.',
    specialAgreements: 'Der Arbeitnehmer wird über alle betrieblichen Angelegenheiten, die ihm im Rahmen oder aus Anlass seiner Tätigkeit in der Firma bekannt geworden sind, auch nach seinem Ausscheiden, Stillschweigen bewahren.',
    
    // Status der kurzfristigen Beschäftigung
    kurzfristigeBeschaeftigung: true,
    kurzfristigeDetails: 'Der Arbeitnehmer versichert dem Arbeitgeber im laufenden Kalenderjahr keine kurzfristige Beschäftigung ausgeübt zu haben, durch die die Grenze von drei Monaten oder 70 Arbeitstagen überschritten wird. Er versichert weiter, dass diese Grenze auch durch das vorliegende Arbeitsverhältnis nicht überschritten wird.',
    
    // Bisherige kurzfristige Beschäftigungen
    previousJobs: [
      { from: '', to: '', totalDays: '', employer: '' }
    ],
    
    // Neue Paragraphen
    verfallsfristen: 'Ansprüche aus diesem Arbeitsverhältnis sind innerhalb einer Frist von drei Monaten nach Fälligkeit in Textform gegenüber der anderen Vertragspartei geltend zu machen. Ansonsten gelten die Ansprüche als verwirkt. Bei einer Ablehnung der Gegenseite oder einer fehlenden Erklärung dieser zwei Wochen nach Geltendmachung kommt es ebenfalls zum Verfall des Anspruchs, wenn keine gerichtliche Geltendmachung stattfindet. Dabei gilt eine einmonatige Frist nach Ablehnung oder Fristablauf.',
    vertragsaenderungen: 'Änderungen, Ergänzungen und Nebenabreden bedürfen der Schriftform; dies gilt auch für die Aufhebung der Schriftform selbst. Sollten einzelne Bestimmungen dieses Vertrages unwirksam sein oder werden, wird hierdurch die Wirksamkeit des Vertrages im Übrigen nicht berührt.',
    
    // Unterschriften
    signatureCity: '',
    employeeSignature: '',
    employeeSignatureDate: '',
    employerSignature: '',
    employerSignatureDate: '',
    parentSignature: '', // für Minderjährige
    parentSignatureDate: '',
    
    // Status
    status: 'draft',
    createdDate: new Date().toLocaleDateString('de-DE'),
    lastModified: new Date().toISOString()
  });

  const [isEditing, setIsEditing] = useState((isTemplate || isEmployee) && !readonly);
  const [isSaving, setIsSaving] = useState(false);

  // ===== Wizard (Schritt-für-Schritt-Ausfüllen, nur für Mitarbeiter) =====
  const [currentStep, setCurrentStep] = useState(1);
  const TOTAL_STEPS = 4;
  const inWizard = isEmployee && isEditing && !readonly;      // 4-Schritt-Wizard nur für Mitarbeiter
  const showPersonalInputs = isEditing && !isEmployee;        // Admin editiert Felder inline; MA nutzt den Wizard

  // Geburtsdatum aus Login-Format (TTMMJJJJ) in Vertrag-Format (TT.MM.JJJJ) bringen
  const formatBirthFromLogin = (b) => {
    if (!b) return '';
    if (typeof b === 'string' && b.includes('.')) return b;
    if (/^\d{8}$/.test(b)) return `${b.slice(0, 2)}.${b.slice(2, 4)}.${b.slice(4)}`;
    return b;
  };

  // Minderjährig? (für Pflicht-Elternunterschrift)
  const isMinor = () => {
    const raw = formData.employeeBirthdate;
    if (!raw) return false;
    let d;
    if (typeof raw === 'string' && raw.includes('.')) {
      const [dd, mm, yy] = raw.split('.');
      d = new Date(`${yy}-${mm}-${dd}`);
    } else {
      d = new Date(raw);
    }
    if (isNaN(d.getTime())) return false;
    const age = (Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000);
    return age < 18;
  };

  // Beim Verlassen des Bearbeitens zurück zu Schritt 1 (nächstes Öffnen startet sauber)
  useEffect(() => {
    if (!isEditing) setCurrentStep(1);
  }, [isEditing]);

  // Signature Canvas refs
  const employeeCanvasRef = useRef(null);
  const parentCanvasRef = useRef(null);
  const [isDrawingEmployee, setIsDrawingEmployee] = useState(false);
  const [isDrawingParent, setIsDrawingParent] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [currentSignatureType, setCurrentSignatureType] = useState('');

  // Contract history state
  const [contractHistory, setContractHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Template states
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [deleteTemplate, setDeleteTemplate] = useState(false);
  const [hasExistingTemplate, setHasExistingTemplate] = useState(false);
  const [showTemplateOptions, setShowTemplateOptions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Prüfung ob Staatsangehörigkeit ein EU-Land ist
  // EU-Erkennung robust: Groß/klein, Leerzeichen und Akzente egal, plus
  // englische/heimische Landesnamen (z.B. "Romania"/"România" = Rumänien).
  // Vorher führte jede abweichende Schreibweise fälschlich zur
  // Arbeitserlaubnis-Pflicht.
  const isEUCountry = (nationality) => {
    const fold = (s) => String(s || '').trim().toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '');
    const norm = fold(nationality);
    if (!norm) return false;
    if (euCountries.some((c) => fold(c) === norm)) return true;
    const EU_ALIASES = new Set([
      'romania', 'rumaenien', 'croatia', 'hrvatska', 'bulgaria', 'poland', 'polska',
      'hungary', 'magyarorszag', 'czech republic', 'czechia', 'cesko', 'tschechische republik',
      'slovakia', 'slovensko', 'slovenia', 'slovenija', 'spain', 'espana', 'sweden', 'sverige',
      'denmark', 'danmark', 'netherlands', 'nederland', 'holland', 'belgium', 'belgie', 'belgique',
      'france', 'italy', 'italia', 'greece', 'ellada', 'hellas', 'austria', 'germany',
      'ireland', 'eire', 'finland', 'suomi', 'estonia', 'eesti', 'latvia', 'latvija',
      'lithuania', 'lietuva', 'luxembourg', 'letzebuerg', 'cyprus', 'kypros', 'portugal', 'malta'
    ]);
    return EU_ALIASES.has(norm);
  };

  // Body-Scroll sperren wenn Signature Modal offen (verhindert Hintergrund-Scrollen auf Mobilgeräten)
  useEffect(() => {
    if (showSignatureModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showSignatureModal]);

  // Prüfung ob Arbeitserlaubnis-Upload nötig ist
  const needsWorkPermit = () => {
    return formData.employeeNationality && !isEUCountry(formData.employeeNationality);
  };

  // Hilfsfunktionen für Datumformatierung
  const formatDateForDisplay = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return '[Datum]';
    // Wenn bereits im deutschen Format, direkt zurückgeben
    if (dateString.includes('.')) return dateString;
    // Ansonsten von ISO-Format konvertieren
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '[Datum]';
      return date.toLocaleDateString('de-DE');
    } catch (error) {
      console.error('Fehler bei Datumformatierung:', error);
      return '[Datum]';
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // Konvertiert YYYY-MM-DD oder DD.MM.YYYY zu YYYY-MM-DD für input[type="date"]
    if (dateString.includes('.')) {
      const parts = dateString.split('.');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
    }
    return dateString;
  };

  // Hilfsfunktion um Date-Objekte in Strings zu konvertieren
  const convertDateObjectsToStrings = (data) => {
    if (!data || typeof data !== 'object') return data;
    const result = { ...data };
    
    // Konvertiere alle möglichen Datumsfelder
    const dateFields = [
      'startDate', 'endDate', 'employeeBirthdate', 'createdDate', 'lastModified',
      'employeeSignatureDate', 'employerSignatureDate', 'parentSignatureDate'
    ];
    
    dateFields.forEach(field => {
      if (result[field]) {
        if (result[field] instanceof Date) {
          // Date-Objekt zu deutschem Format
          result[field] = result[field].toLocaleDateString('de-DE');
        } else if (typeof result[field] === 'string' && result[field].includes('T')) {
          // ISO-String zu deutschem Format
          try {
            const date = new Date(result[field]);
            if (!isNaN(date.getTime())) {
              result[field] = date.toLocaleDateString('de-DE');
            }
          } catch (error) {
            console.warn(`Fehler bei Konvertierung von ${field}:`, error);
          }
        }
      }
    });
    
    // Konvertiere Daten in der previousJobs Tabelle
    if (result.previousJobs && Array.isArray(result.previousJobs)) {
      result.previousJobs = result.previousJobs.map(job => {
        if (!job || typeof job !== 'object') return job;
        const convertedJob = { ...job };
        
        // Konvertiere from und to Felder
        ['from', 'to'].forEach(field => {
          if (convertedJob[field]) {
            if (convertedJob[field] instanceof Date) {
              convertedJob[field] = convertedJob[field].toLocaleDateString('de-DE');
            } else if (typeof convertedJob[field] === 'string' && convertedJob[field].includes('T')) {
              try {
                const date = new Date(convertedJob[field]);
                if (!isNaN(date.getTime())) {
                  convertedJob[field] = date.toLocaleDateString('de-DE');
                }
              } catch (error) {
                console.warn(`Fehler bei Konvertierung von previousJob.${field}:`, error);
              }
            }
          }
        });
        
        return convertedJob;
      });
    }
    
    return result;
  };

  const handleDateChange = (field, value) => {
    // Speichere das Datum als DD.MM.YYYY für die Anzeige und Backend
    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        const formattedDate = date.toLocaleDateString('de-DE');
        handleInputChange(field, formattedDate);
      }
    } else {
      handleInputChange(field, '');
    }
  };

  useEffect(() => {
    if (contractData) {
      console.log('Loading contractData:', contractData);
      // Wenn Mitarbeiterdaten vorhanden sind, diese verwenden und Date-Objekte konvertieren
      const safeContractData = migrateAddress(convertDateObjectsToStrings(contractData));
      console.log('Converted contractData:', safeContractData);
      
      // Für Mitarbeiter: Template-Daten für Start-/Enddatum bevorzugen
      if (isEmployee && templateData) {
        console.log('Merging contractData with templateData for dates');
        const templateWithConvertedDates = convertDateObjectsToStrings(templateData);
        setFormData(prev => ({ 
          ...prev, 
          ...safeContractData,
          // Template-Daten für kritische Felder überschreiben
          startDate: templateWithConvertedDates.startDate || safeContractData.startDate,
          endDate: templateWithConvertedDates.endDate || safeContractData.endDate,
          position: templateWithConvertedDates.position || safeContractData.position,
          salary: templateWithConvertedDates.salary || safeContractData.salary,
          salaryType: templateWithConvertedDates.salaryType || safeContractData.salaryType
        }));
      } else {
        setFormData(prev => ({ ...prev, ...safeContractData }));
      }
    } else if (isEmployee && templateData) {
      console.log('Loading templateData:', templateData);
      // Wenn kein Mitarbeitervertrag vorhanden, aber Vorlage da ist, diese als Basis verwenden
      const templateWithConvertedDates = convertDateObjectsToStrings(templateData);
      setFormData(prev => ({
        ...prev,
        ...templateWithConvertedDates,
        // Überschreibe mit Mitarbeiterdaten
        employeeName: `${user?.firstname || ''} ${user?.lastname || ''}`.trim(),
        employeeBirthdate: formatBirthFromLogin(user?.birthdate),
        // Setze Mitarbeiter-spezifische Felder zurück
        employeeStreet: '',
        employeeHouseNumber: '',
        employeePLZ: '',
        employeeCity: '',
        employeeAddress: '',
        employeeNationality: '',
        socialSecurityNumber: '',
        birthPlace: '',
        taxId: '',
        iban: '',
        accountHolder: '',
        // Zurücksetzen der vorherigen Jobs für neue Mitarbeiter
        previousJobs: [{ from: '', to: '', totalDays: '', employer: '' }],
        lastModified: new Date().toISOString()
      }));
    } else if (isEmployee && user) {
      // Fallback: Nur Mitarbeiterdaten ausfüllen wenn kein contractData und keine templateData vorhanden
      setFormData(prev => ({
        ...prev,
        employeeName: `${user?.firstname || ''} ${user?.lastname || ''}`.trim(),
        employeeBirthdate: formatBirthFromLogin(user?.birthdate),
        lastModified: new Date().toISOString()
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractData, templateData, isEmployee, user]);

  // Separater useEffect um Vorlage zu laden wenn keine templateData übergeben wurden
  useEffect(() => {
    if (isEmployee && !templateData && !contractData) {
      console.log('Loading contract template for employee...');
      apiCall('/contract-template')
        .then(template => {
          console.log('Loaded template from API:', template);
          if (template && template.position) {
            // Konvertiere Backend-Format zu Frontend-Format
            const frontendTemplate = {
              employerName: template.employer?.name || 'Bavarian Concept Group GmbH',
              employerAddress: template.employer?.address || 'Bürkelweg 18\n85540 Haar',
              position: template.position?.title || 'Unterstützung bei der Gepäckaufbewahrung auf dem Oktoberfest in München',
              startDate: template.position?.startDate || '19.09.2026',
              endDate: template.position?.endDate || '04.10.2026',
              salary: template.salary?.amount || '15',
              salaryType: template.salary?.type || 'stundenlohn',
              workingTime: template.salary?.workingTime || 
                          (typeof template.workingTime?.regular === 'string' ? template.workingTime.regular : 'durchschnittlich 8 Stunden pro Tag'),
              breakTime: typeof template.workingTime?.breaks === 'string' ? template.workingTime.breaks : 
                        'Bei einer Arbeitszeit von mehr als 6 Stunden steht eine Pause von 30 Minuten zu.',
              vacationDays: typeof template.workingTime?.vacation === 'string' ? template.workingTime.vacation : 
                           'Bei dieser kurzfristigen Beschäftigung entsteht kein Urlaubsanspruch.',
              specialAgreements: template.documents?.specialAgreements || 'Der Arbeitnehmer verpflichtet sich, über alle betrieblichen Angelegenheiten Stillschweigen zu bewahren.',
              kurzfristigeDetails: template.documents?.kurzfristigeDetails || 'Der Arbeitnehmer versichert dem Arbeitgeber im laufenden Kalenderjahr keine kurzfristige Beschäftigung ausgeübt zu haben, durch die die Grenze von drei Monaten oder 70 Arbeitstagen überschritten wird.',
            };
            
            console.log('Setting form data with template:', frontendTemplate);
            setFormData(prev => ({
              ...prev,
              ...frontendTemplate,
              // Überschreibe mit Mitarbeiterdaten
              employeeName: `${user?.firstname || ''} ${user?.lastname || ''}`.trim(),
              employeeBirthdate: formatBirthFromLogin(user?.birthdate),
              lastModified: new Date().toISOString()
            }));
          }
        })
        .catch(error => {
          console.error('Fehler beim Laden der Vorlage:', error);
        });
    }
  }, [isEmployee, templateData, contractData, user]);

  // Separater useEffect für Mitarbeiter: Lade immer die aktuelle Vorlage für kritische Daten
  useEffect(() => {
    if (isEmployee && !templateData) {
      console.log('Loading current template data for employee to update contract dates...');
      apiCall('/contract-template')
        .then(template => {
          console.log('Loaded current template for date updates:', template);
          if (template && template.position) {
            // Überschreibe nur die kritischen Template-Felder
            setFormData(prev => ({
              ...prev,
              startDate: template.position?.startDate || prev.startDate,
              endDate: template.position?.endDate || prev.endDate,
              position: template.position?.title || prev.position,
              salary: template.salary?.amount || prev.salary,
              salaryType: template.salary?.type || prev.salaryType
            }));
            console.log('Updated contract with current template dates:', {
              startDate: template.position?.startDate,
              endDate: template.position?.endDate
            });
          }
        })
        .catch(error => {
          console.error('Fehler beim Laden der aktuellen Vorlage:', error);
        });
    }
  }, [isEmployee, templateData, contractData]); // Läuft auch wenn contractData vorhanden ist

  // Compute combined address from individual fields
  const computeAddress = (data) => {
    const s = data.employeeStreet || '';
    const n = data.employeeHouseNumber || '';
    const p = data.employeePLZ || '';
    const c = data.employeeCity || '';
    const streetPart = (s + ' ' + n).trim();
    const cityPart = (p + ' ' + c).trim();
    return [streetPart, cityPart].filter(Boolean).join(', ');
  };

  // Migrate old single employeeAddress to new split fields
  const migrateAddress = (data) => {
    if (data.employeeAddress && !data.employeeStreet && !data.employeeCity) {
      const parts = data.employeeAddress.split(',');
      if (parts.length >= 2) {
        const streetPart = parts[0].trim();
        const cityPart = parts.slice(1).join(',').trim();
        const streetMatch = streetPart.match(/^(.+?)\s+(\d+\S*)$/);
        if (streetMatch) {
          data.employeeStreet = streetMatch[1];
          data.employeeHouseNumber = streetMatch[2];
        } else {
          data.employeeStreet = streetPart;
        }
        const cityMatch = cityPart.match(/^(\d{4,5})\s+(.+)$/);
        if (cityMatch) {
          data.employeePLZ = cityMatch[1];
          data.employeeCity = cityMatch[2];
        } else {
          data.employeeCity = cityPart;
        }
      } else {
        data.employeeStreet = data.employeeAddress;
      }
    }
    return data;
  };

  // Format IBAN with spaces every 4 characters
  const formatIBAN = (value) => {
    const cleaned = value.replace(/\s/g, '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    return cleaned.replace(/(.{4})/g, '$1 ').trim();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value,
        lastModified: new Date().toISOString()
      };
      // Auto-compute employeeAddress when address fields change
      if (['employeeStreet', 'employeeHouseNumber', 'employeePLZ', 'employeeCity'].includes(field)) {
        updated.employeeAddress = computeAddress(updated);
      }
      return updated;
    });
  };

  const addPreviousJob = () => {
    setFormData(prev => ({
      ...prev,
      previousJobs: [...prev.previousJobs, { from: '', to: '', totalDays: '', employer: '' }],
      lastModified: new Date().toISOString()
    }));
  };

  const removePreviousJob = (index) => {
    setFormData(prev => {
      const remaining = prev.previousJobs.filter((_, i) => i !== index);
      return {
        ...prev,
        previousJobs: remaining,
        // Keine Beschäftigung mehr übrig → "keine kurzfristige Beschäftigung" annehmen,
        // sonst hängt man in einem ungültigen Zustand fest.
        noPreviousJobs: remaining.length === 0 ? true : prev.noPreviousJobs,
        lastModified: new Date().toISOString()
      };
    });
  };

  // "keine kurzfristige Beschäftigung" umschalten. Beim Abwählen direkt eine leere
  // Zeile anbieten, damit man nicht im ungültigen Zustand (leer + nicht angehakt) landet.
  const setNoPreviousJobs = (checked) => {
    setFormData(prev => ({
      ...prev,
      noPreviousJobs: checked,
      previousJobs: checked
        ? prev.previousJobs
        : (prev.previousJobs && prev.previousJobs.length ? prev.previousJobs : [{ from: '', to: '', totalDays: '', employer: '' }]),
      lastModified: new Date().toISOString()
    }));
  };

  // "15.04.2026" → Date, sonst null
  const parseDE = (str) => {
    const m = String(str || '').trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/);
    if (!m) return null;
    const y = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    const d = new Date(y, Number(m[2]) - 1, Number(m[1]));
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const updatePreviousJob = (index, field, value) => {
    setFormData(prev => {
      const newJobs = [...prev.previousJobs];
      newJobs[index] = { ...newJobs[index], [field]: value };
      // Kalendertage als Vorschlag, wenn beide Daten da sind und noch keine Tage stehen
      if ((field === 'from' || field === 'to') && !String(newJobs[index].totalDays || '').trim()) {
        const a = parseDE(newJobs[index].from), b = parseDE(newJobs[index].to);
        if (a && b && b >= a) newJobs[index].totalDays = String(Math.round((b - a) / 86400000) + 1);
      }
      return {
        ...prev,
        previousJobs: newJobs,
        lastModified: new Date().toISOString()
      };
    });
  };

  const handleSave = async (isDraft = true, templateOptions = {}) => {
    if (!onSave) return;
    
    setIsSaving(true);
    try {
      if (isEmployee) {
        // Für Mitarbeiter: verschiedene Save-Modi
        const status = isDraft ? 'draft' : 'submitted';
        await onSave(formData, status, templateOptions);
        setIsEditing(false);
        
        // Nach erfolgreichem Submit Template-Optionen zurücksetzen
        if (!isDraft) {
          setSaveAsTemplate(false);
          setDeleteTemplate(false);
          setShowTemplateOptions(false);
          // Template-Status neu laden
          loadEmployeeTemplate();
        }
      } else {
        // Für Admin-Template
        await onSave(formData);
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      notify(t('contract.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  // ===== Auto-Speichern als Entwurf (Mitarbeiter) =====
  const [autoSaved, setAutoSaved] = useState(false);
  const autoSaveDraft = async () => {
    if (!onSave || !isEmployee) return;
    try {
      await onSave(formData, 'draft', { silent: true });
      setAutoSaved(true);
    } catch (e) { /* evtl. noch unvollständig — ignorieren, nächster Versuch folgt */ }
  };
  useEffect(() => {
    if (!isEmployee || !isEditing) return;
    if (!formData.employeeName || !formData.employeeName.trim()) return; // nicht im Leerzustand speichern
    setAutoSaved(false);
    const timer = setTimeout(() => { autoSaveDraft(); }, 2500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, isEmployee, isEditing]);

  // ===== Wizard: Per-Schritt-Validierung =====
  const stepFields = {
    1: ['employeeName', 'employeeStreet', 'employeePLZ', 'employeeCity', 'employeeBirthdate', 'employeeNationality', 'birthPlace'],
    2: ['socialSecurityNumber', 'taxId', 'iban'],
  };

  const scrollToField = (field) => {
    const el = document.getElementById('cf-' + field);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    try { el.focus({ preventScroll: true }); } catch (_) {}
    el.style.boxShadow = '0 0 0 3px rgba(220,53,69,0.55)';
    el.style.borderColor = '#dc3545';
    setTimeout(() => { el.style.boxShadow = ''; el.style.borderColor = ''; }, 2800);
  };

  // Kurzfristige Beschäftigungen: Entweder „keine" angehakt ODER mindestens eine
  // VOLLSTÄNDIGE Zeile (Von, Bis, Gesamttage, Arbeitgeber). Halb ausgefüllte
  // Zeilen (z.B. nur "InStaff" ohne Daten) blocken die Einreichung.
  // Komplett leere Zusatz-Zeilen werden ignoriert.
  const validatePreviousJobs = () => {
    if (formData.noPreviousJobs) return null;
    const jobs = formData.previousJobs || [];
    const isEmpty = (j) => !j.from && !j.to && !String(j.totalDays || '').trim() && !(j.employer && j.employer.trim());
    const isComplete = (j) => j.from && j.to && String(j.totalDays || '').trim() !== '' && j.employer && j.employer.trim();
    if (jobs.some((j) => !isEmpty(j) && !isComplete(j))) return t('contract.prevJobsIncomplete');
    if (!jobs.some(isComplete)) return t('contract.fillPrevJobs');
    return null;
  };

  const validateStep = (step) => {
    const missing = (stepFields[step] || []).filter(f => !formData[f] || String(formData[f]).trim() === '');
    if (missing.length) { notify(t('contract.fillRequired')); scrollToField(missing[0]); return false; }
    if (step === 2) {
      if (!formData.socialBenefitStatus) { notify(t('contract.selectSocialBenefits')); return false; }
      const prevJobsError = validatePreviousJobs();
      if (prevJobsError) { notify(prevJobsError); return false; }
    }
    if (step === 3) {
      if (!formData.idCardImage) { notify(t('contract.uploadIdCard')); return false; }
      if (needsWorkPermit() && !formData.workPermitDocument) { notify(t('contract.uploadWorkPermit')); return false; }
      if (needsWorkPermit() && !formData.residencePermitDocument) { notify(t('contract.uploadResidence')); return false; }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) setCurrentStep(s => Math.min(s + 1, TOTAL_STEPS));
  };

  const handleSubmit = async () => {
    if (isSubmitting) {
      console.log('⚠️ Already submitting, ignoring duplicate request');
      return;
    }
    
    // Alle Pflichtfelder prüfen (außer accountHolder)
    const requiredFields = [
      { field: 'employeeName', label: t('contract.reqName') },
      { field: 'employeeStreet', label: t('contract.reqStreet') },
      { field: 'employeePLZ', label: t('contract.reqPLZ') },
      { field: 'employeeCity', label: t('contract.reqCity') },
      { field: 'employeeBirthdate', label: t('contract.reqBirthdate') },
      { field: 'employeeNationality', label: t('contract.reqNationality') },
      { field: 'socialSecurityNumber', label: t('contract.reqSocialSec') },
      { field: 'birthPlace', label: t('contract.reqBirthPlace') },
      { field: 'taxId', label: t('contract.reqTaxId') },
      { field: 'iban', label: t('contract.reqIban') },
      { field: 'signatureCity', label: t('contract.reqPlace') },
    ];
    
    const missingFields = requiredFields.filter(f => !formData[f.field] || formData[f.field].trim() === '');
    if (missingFields.length > 0) {
      notify(t('contract.fillRequired') + ': ' + missingFields.map(f => f.label).join(', '));
      // Zum ersten fehlenden Feld scrollen, fokussieren und kurz hervorheben
      const el = document.getElementById('cf-' + missingFields[0].field);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        try { el.focus({ preventScroll: true }); } catch (_) {}
        el.style.boxShadow = '0 0 0 3px rgba(220,53,69,0.55)';
        el.style.borderColor = '#dc3545';
        setTimeout(() => { el.style.boxShadow = ''; el.style.borderColor = ''; }, 2800);
      }
      return;
    }
    
    if (!formData.idCardImage) {
      notify(t('contract.uploadIdCard'));
      return;
    }
    
    if (!formData.socialBenefitStatus) {
      notify(t('contract.selectSocialBenefits'));
      return;
    }
    
    if (needsWorkPermit() && !formData.workPermitDocument) {
      notify(t('contract.uploadWorkPermit'));
      return;
    }

    if (needsWorkPermit() && !formData.residencePermitDocument) {
      notify(t('contract.uploadResidence'));
      return;
    }
    
    // Prüfung: Entweder "keine Beschäftigung" angehakt ODER mindestens eine
    // VOLLSTÄNDIG ausgefüllte Beschäftigung (keine halben Zeilen)
    const prevJobsError = validatePreviousJobs();
    if (prevJobsError) {
      notify(prevJobsError);
      return;
    }
    
    if (!formData.employeeSignature) {
      notify(t('contract.signFirst'));
      return;
    }

    // Minderjährige: Unterschrift eines Erziehungsberechtigten erforderlich
    if (isMinor() && !formData.parentSignature) {
      notify(t('contract.parentSignRequired'));
      return;
    }

    // Zeige Template-Optionen vor dem Submit
    if (isEmployee && !showTemplateOptions) {
      setShowTemplateOptions(true);
    } else {
      setIsSubmitting(true);
      console.log('📤 Starting contract submission...');
      
      try {
        // Submit mit Template-Optionen
        const templateOptions = {
          saveAsTemplate,
          deleteTemplate
        };
        const success = await handleSave(false, templateOptions); // Submit final version
        
        if (success) {
          console.log('✅ Contract submitted successfully');
          setShowTemplateOptions(false);
        }
      } catch (error) {
        console.error('❌ Submit error:', error);
        notify(t('contract.submitError'));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Load contract history for the user
  const loadContractHistory = async () => {
    if (!isEmployee || !user?.id) {
      console.log('💡 Contract history not loaded - not employee or no user ID:', { isEmployee, userId: user?.id });
      return;
    }
    
    console.log('🔍 Loading contract history for user:', user.id);
    setIsLoadingHistory(true);
    try {
      const data = await apiCall(`/employee-contracts/history/${user.id}`);
      console.log('📋 Contract history data:', data);
      setContractHistory(data);
    } catch (error) {
      console.error('💥 Error loading contract history:', error);
      setContractHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Load contract history when component mounts for employees
  useEffect(() => {
    if (isEmployee && user?.id) {
      loadContractHistory();
      loadEmployeeTemplate();
    }
  }, [isEmployee, user?.id]);

  // Load employee template
  const loadEmployeeTemplate = async () => {
    if (!isEmployee || !user?.id) return;
    
    try {
      const template = await apiCall(`/employee-contract-template/${user.id}`);
      if (template && template.templateData) {
        setHasExistingTemplate(true);
        console.log('📄 Found existing template for user');
      } else {
        setHasExistingTemplate(false);
        console.log('📄 No existing template found');
      }
    } catch (error) {
      console.error('Error loading template:', error);
      setHasExistingTemplate(false);
    }
  };

  // Use existing template to prefill form
  const useExistingTemplate = async () => {
    if (!isEmployee || !user?.id) return;
    
    try {
      const template = await apiCall(`/employee-contract-template/${user.id}`);
      if (template && template.templateData) {
        setFormData(prevData => ({
          ...prevData,
          ...template.templateData
        }));
        console.log('✅ Template data loaded into form');
      }
    } catch (error) {
      console.error('Error using template:', error);
      notify('Fehler beim Laden der Vorlage');
    }
  };

  // Signature functions
  const openSignatureModal = (type) => {
    // Der Unterschriftsort ist Pflicht, wurde aber praktisch von ALLEN übersehen:
    // Das Eingabefeld stand weiter oben im Dokument und erschien nur im Bearbeiten-
    // Modus, der Unterschreiben-Knopf ging aber immer. Ergebnis: unterschrieben,
    // „Einreichen" gedrückt, Fehlermeldung zu einem Feld, das gar nicht sichtbar
    // war. Jetzt wird der Ort hier gleich mit abgefragt – vorbelegt mit dem
    // Wohnort, änderbar.
    if (!String(formData.signatureCity || '').trim() && String(formData.employeeCity || '').trim()) {
      handleInputChange('signatureCity', String(formData.employeeCity).trim());
    }
    setCurrentSignatureType(type);
    setShowSignatureModal(true);
  };

  // Koordinaten aus Mouse- oder Touch-Event extrahieren.
  // WICHTIG: auf Skalierung (canvas.width vs. angezeigte Breite) umrechnen,
  // sonst zeichnet man auf dem Handy (skaliertes Canvas) verschoben/verzerrt.
  const getEventCoords = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const point = (e.touches && e.touches.length > 0) ? e.touches[0] : e;
    return {
      x: (point.clientX - rect.left) * scaleX,
      y: (point.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e, canvasRef, setIsDrawing) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getEventCoords(e, canvas);
    
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e, canvasRef, isDrawing) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getEventCoords(e, canvas);
    
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (e, setIsDrawing) => {
    if (e) e.preventDefault();
    setIsDrawing(false);
  };

  const clearCanvas = (canvasRef) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  // Prüft, ob auf dem Canvas tatsächlich etwas gezeichnet wurde.
  // Ohne diese Prüfung wird eine leere Leinwand als "Unterschrift" gespeichert
  // und der Vertrag gilt fälschlich als unterschrieben.
  const canvasHasInk = (canvas) => {
    const ctx = canvas.getContext('2d');
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > 0) return true;
    }
    return false;
  };

  const saveSignature = () => {
    let canvas;
    if (currentSignatureType === 'employee') {
      canvas = employeeCanvasRef.current;
    } else if (currentSignatureType === 'parent') {
      canvas = parentCanvasRef.current;
    }

    if (canvas && !canvasHasInk(canvas)) {
      notify(t('contract.signatureEmpty'));
      return;
    }

    if (canvas) {
      const signatureData = canvas.toDataURL();
      const currentDate = new Date().toLocaleDateString('de-DE');
      
      if (currentSignatureType === 'employee') {
        handleInputChange('employeeSignature', signatureData);
        handleInputChange('employeeSignatureDate', currentDate);
      } else if (currentSignatureType === 'parent') {
        handleInputChange('parentSignature', signatureData);
        handleInputChange('parentSignatureDate', currentDate);
      }
    }
    
    setShowSignatureModal(false);
  };

  const uploadSignature = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const currentDate = new Date().toLocaleDateString('de-DE');
        
        if (type === 'employee') {
          handleInputChange('employeeSignature', event.target.result);
          handleInputChange('employeeSignatureDate', currentDate);
        } else if (type === 'parent') {
          handleInputChange('parentSignature', event.target.result);
          handleInputChange('parentSignatureDate', currentDate);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // PDF-Export-Funktion mit Browser-nativem Print
  const handlePrintPDF = () => {
    if (onPrintPDF) {
      onPrintPDF(formData);
    } else {
      // Fallback: Browser-Print
      window.print();
    }
  };

  return (
    <>
      {/* Print CSS für PDF-Export */}
      <style>
        {`
          /* Standard CSS für bessere PDF-Darstellung */
          .contract-body {
            color: #000000 !important;
            background-color: #ffffff !important;
            font-weight: normal !important;
          }
          
          .contract-body *,
          .contract-body p,
          .contract-body div,
          .contract-body span,
          .contract-body input,
          .contract-body textarea,
          .contract-body label,
          .contract-body small,
          .contract-body .form-control-plaintext,
          .contract-body .text-muted {
            color: #000000 !important;
            opacity: 1 !important;
            font-weight: normal !important;
          }
          
          .contract-body h1, .contract-body h2, .contract-body h3, .contract-body h4, .contract-body h5, .contract-body h6 {
            color: #000000 !important;
            font-weight: bold !important;
            opacity: 1 !important;
          }
          
          .contract-body strong, .contract-body b {
            color: #000000 !important;
            font-weight: bold !important;
            opacity: 1 !important;
          }
          
          .contract-body .form-control-plaintext {
            color: #000000 !important;
            border-bottom: 1px solid #000000 !important;
            background-color: transparent !important;
            opacity: 1 !important;
          }
          
          .contract-body .bg-light {
            background-color: #f8f9fa !important;
            border: 1px solid #dee2e6 !important;
            color: #000000 !important;
          }

          .contract-body .text-muted {
            color: #000000 !important;
            opacity: 1 !important;
          }

          /* Spezielle Regeln für bessere Sichtbarkeit */
          .contract-body input[readonly] {
            color: #000000 !important;
            background-color: #ffffff !important;
            border: 1px solid #000000 !important;
            opacity: 1 !important;
          }

          @media print {
            .container-fluid { max-width: none !important; margin: 0 !important; padding: 0 !important; }
            .d-flex.justify-content-between { display: none !important; }
            .btn { display: none !important; }
            .modal { display: none !important; }
            .card { border: none !important; box-shadow: none !important; margin: 0 !important; }
            .card-body { padding: 20px !important; }
            body { font-size: 12pt !important; line-height: 1.3 !important; }
            h4 { font-size: 14pt !important; margin-bottom: 15px !important; }
            p, div { margin-bottom: 8px !important; }
            .form-control-plaintext { border: none !important; padding: 2px 0 !important; background: transparent !important; }
            .form-group { margin-bottom: 10px !important; }
            .bg-light { background-color: #f8f9fa !important; }
            .signature-container { page-break-inside: avoid !important; }
            table { page-break-inside: avoid !important; font-size: 11pt !important; }
            .page-break { page-break-before: always !important; }
          }
        `}
      </style>
      
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              {/* Bildschirm: "Digitaler Arbeitsvertrag / Vertrag zum Ausfüllen".
                  PDF (no-pdf/pdf-only): "Arbeitsvertrag" + Firma und Saison. */}
              <h2>
                <i className="bi bi-file-earmark-text me-2 text-primary no-pdf"></i>
                <span className="no-pdf">{isTemplate ? t('contract.templateTitle') : t('contract.title')}</span>
                <span className="pdf-only" style={{ display: 'none' }}>Arbeitsvertrag</span>
              </h2>
              <small className="text-muted no-pdf">
                {isTemplate ? t('contract.templateSubtitle') : t('contract.formSubtitle')}
              </small>
              <small className="text-muted pdf-only" style={{ display: 'none' }}>
                {formData.employerName || 'Bavarian Concept Group GmbH'} · Oktoberfest {String(formData.startDate || '').slice(-4) || new Date().getFullYear()}
              </small>
              
              {/* Template Load Button für Mitarbeiter */}
              {isEmployee && hasExistingTemplate && (
                <div className="mt-2">
                  <button 
                    className="btn btn-sm btn-outline-info"
                    onClick={useExistingTemplate}
                  >
                    <i className="bi bi-file-earmark-arrow-down me-1"></i>
                    {t('contract.loadTemplate')}
                  </button>
                </div>
              )}
            </div>
            <div>
              {user.role === 'admin' && !readonly && (
                <>
                  {isEditing ? (
                    <div>
                      <button 
                        className="btn btn-success me-2" 
                        onClick={() => handleSave(true)}
                        disabled={isSaving}
                      >
                        <i className="bi bi-save me-2"></i>
                        {isSaving ? t('contract.saving') : t('contract.save')}
                      </button>
                      <button 
                        className="btn btn-secondary" 
                        onClick={() => setIsEditing(false)}
                      >
                        <i className="bi bi-x-lg me-2"></i>
                        {t('contract.cancel')}
                      </button>
                    </div>
                  ) : (
                    <button 
                      className="btn btn-warning" 
                      onClick={() => setIsEditing(true)}
                    >
                      <i className="bi bi-pencil me-2"></i>
                      {t('contract.edit')}
                    </button>
                  )}
                </>
              )}
              
              {isEmployee && !readonly && (
                <div>
                  {!isEditing && (
                    <div>
                      <button 
                        className="btn btn-warning me-2" 
                        onClick={() => setIsEditing(true)}
                      >
                        <i className="bi bi-pencil me-2"></i>
                        {contractData ? t('contract.editContract') : t('contract.fillContract')}
                      </button>
                      <button 
                        className="btn btn-outline-primary" 
                        onClick={handlePrintPDF}
                      >
                        <i className="bi bi-file-earmark-pdf me-2"></i>
                        {t('contract.printPdf')}
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              {/* PDF Export Button für alle Benutzer im readonly-Modus */}
              {readonly && (
                <button 
                  className="btn btn-outline-primary" 
                  onClick={handlePrintPDF}
                >
                  <i className="bi bi-file-earmark-pdf me-2"></i>
                  {t('contract.printPdf')}
                </button>
              )}
            </div>
          </div>

          {/* Contract Form */}
          <div className="card shadow">
            <div className="card-body contract-body">

              {/* ===== Schritt-für-Schritt-Wizard (nur Mitarbeiter) ===== */}
              {inWizard && (
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                    <span className="badge bg-primary fs-6">{t('contract.step')} {currentStep} / {TOTAL_STEPS}</span>
                    {autoSaved && <span className="text-success small"><i className="bi bi-check-circle-fill me-1"></i>{t('contract.autoSaved')}</span>}
                    <select className="form-select form-select-sm" style={{ width: 'auto' }}
                      value={language} onChange={(e) => setLanguage(e.target.value)}>
                      {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.flag} {l.name}</option>)}
                    </select>
                  </div>
                  <div className="progress mb-4" style={{ height: '8px' }}>
                    <div className="progress-bar bg-success" role="progressbar"
                      style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }} />
                  </div>

                  {currentStep === 1 && (
                    <div>
                      <h5 className="fw-bold mb-3">1. {t('contract.stepPersonal')}</h5>
                      <div className="row g-3">
                        <div className="col-12">
                          <label className="form-label">{t('contract.nameLabel')} *</label>
                          <input id="cf-employeeName" className="form-control" value={formData.employeeName || ''}
                            onChange={(e) => handleInputChange('employeeName', e.target.value)} />
                        </div>
                        <div className="col-md-8">
                          <label className="form-label">{t('contract.wzStreet')} *</label>
                          <input id="cf-employeeStreet" className="form-control" value={formData.employeeStreet || ''}
                            onChange={(e) => handleInputChange('employeeStreet', e.target.value)} />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">{t('contract.wzHouseNr')}</label>
                          <input className="form-control" value={formData.employeeHouseNumber || ''}
                            onChange={(e) => handleInputChange('employeeHouseNumber', e.target.value)} />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">{t('contract.wzPlz')} *</label>
                          <input id="cf-employeePLZ" className="form-control" value={formData.employeePLZ || ''}
                            onChange={(e) => handleInputChange('employeePLZ', e.target.value)} />
                        </div>
                        <div className="col-md-8">
                          <label className="form-label">{t('contract.wzCity')} *</label>
                          <input id="cf-employeeCity" className="form-control" value={formData.employeeCity || ''}
                            onChange={(e) => handleInputChange('employeeCity', e.target.value)} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">{t('contract.wzBirthdate')} *</label>
                          <input id="cf-employeeBirthdate" className="form-control" inputMode="numeric" placeholder="TT.MM.JJJJ"
                            value={formData.employeeBirthdate || ''}
                            onChange={(e) => handleInputChange('employeeBirthdate', e.target.value)}
                            onBlur={(e) => { const f = fmtBirthDE(e.target.value); if (f !== e.target.value) handleInputChange('employeeBirthdate', f); }} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">{t('contract.wzBirthPlace')} *</label>
                          <input id="cf-birthPlace" className="form-control" value={formData.birthPlace || ''}
                            onChange={(e) => handleInputChange('birthPlace', e.target.value)} />
                        </div>
                        <div className="col-12">
                          <label className="form-label">{t('contract.wzNationality')} *</label>
                          <select id="cf-employeeNationality" className="form-select" value={formData.employeeNationality || ''}
                            onChange={(e) => handleInputChange('employeeNationality', e.target.value)}>
                            <option value="">{t('contract.pleaseSelect')}</option>
                            {euCountries.map((c) => <option key={c} value={c}>{c}</option>)}
                            {nonEuCountries.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {currentStep === 2 && (
                    <div>
                      <h5 className="fw-bold mb-3">2. {t('contract.stepTax')}</h5>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">{t('contract.socialSecNr')} *</label>
                          <input id="cf-socialSecurityNumber" className="form-control" value={formData.socialSecurityNumber || ''}
                            onChange={(e) => handleInputChange('socialSecurityNumber', e.target.value)} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">{t('contract.wzTaxId')} *</label>
                          <input id="cf-taxId" className="form-control" value={formData.taxId || ''}
                            onChange={(e) => handleInputChange('taxId', e.target.value)} />
                        </div>
                        <div className="col-md-8">
                          <label className="form-label">IBAN *</label>
                          <input id="cf-iban" className="form-control font-monospace" value={formData.iban || ''}
                            onChange={(e) => handleInputChange('iban', formatIBAN(e.target.value))}
                            placeholder="DE00 0000 0000 0000 0000 00" />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">{t('contract.wzAccountHolder')}</label>
                          <input className="form-control" value={formData.accountHolder || ''}
                            onChange={(e) => handleInputChange('accountHolder', e.target.value)} />
                        </div>
                        <div className="col-12">
                          <label className="form-label">{t('contract.wzSocialBenefits')} *</label>
                          <select className="form-select" value={formData.socialBenefitStatus || ''}
                            onChange={(e) => handleInputChange('socialBenefitStatus', e.target.value)}>
                            <option value="">{t('contract.pleaseSelect')}</option>
                            <option value="grundsicherung">{t('contract.basicSecurity')}</option>
                            <option value="arbeitslosengeld">{t('contract.unemployment')}</option>
                            <option value="rente">{t('contract.pension')}</option>
                            <option value="keine">{t('contract.noBenefits')}</option>
                          </select>
                        </div>
                        <div className="col-12">
                          <label className="form-label">{t('contract.wzPrevJobs')}</label>
                          <div className="form-check mb-2">
                            <input className="form-check-input" type="checkbox" id="wiz-nojobs"
                              checked={formData.noPreviousJobs || false}
                              onChange={(e) => setNoPreviousJobs(e.target.checked)} />
                            <label className="form-check-label" htmlFor="wiz-nojobs">{t('contract.wzNoPrevJobs')}</label>
                          </div>
                          {!formData.noPreviousJobs && (
                            <div>
                              {(formData.previousJobs || []).map((job, index) => (
                                <div className="row g-2 mb-2 align-items-end" key={index}>
                                  <div className="col-md-4">
                                    <input className="form-control form-control-sm" placeholder={t('contract.wzEmployer')}
                                      value={job.employer || ''} onChange={(e) => updatePreviousJob(index, 'employer', e.target.value)} />
                                  </div>
                                  <div className="col-md-3">
                                    <input className="form-control form-control-sm" placeholder="von (TT.MM.JJJJ)"
                                      value={job.from || ''} onChange={(e) => updatePreviousJob(index, 'from', e.target.value)} />
                                  </div>
                                  <div className="col-md-2">
                                    <input className="form-control form-control-sm" placeholder="bis"
                                      value={job.to || ''} onChange={(e) => updatePreviousJob(index, 'to', e.target.value)} />
                                  </div>
                                  {/* Gesamttage = tatsächlich gearbeitete Tage (70-Tage-Regel). Die
                                      Prüfung verlangte das Feld schon immer, im Wizard fehlte es aber –
                                      jeder mit Vorbeschäftigung blieb hängen. Wird aus den Daten
                                      vorbelegt und kann korrigiert werden. */}
                                  <div className="col-md-2">
                                    <input className="form-control form-control-sm" placeholder="Tage" type="number" min="1"
                                      title="Tatsächlich gearbeitete Tage" value={job.totalDays || ''}
                                      onChange={(e) => updatePreviousJob(index, 'totalDays', e.target.value)} />
                                  </div>
                                  <div className="col-md-1">
                                    <button type="button" className="btn btn-outline-danger btn-sm w-100"
                                      onClick={() => removePreviousJob(index)}>×</button>
                                  </div>
                                </div>
                              ))}
                              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addPreviousJob}>
                                + {t('contract.wzAddJob')}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {currentStep === 3 && (
                    <div>
                      <h5 className="fw-bold mb-3">3. {t('contract.stepDocuments')}</h5>
                      <div className="mb-4">
                        <label className="form-label fw-bold">{t('contract.wzIdCard')} *</label>
                        {formData.idCardImage ? (
                          <div className="d-flex align-items-center gap-2">
                            <DocView src={formData.idCardImage} alt="Ausweis" maxHeight={90} />
                            <button type="button" className="btn btn-outline-danger btn-sm"
                              onClick={() => handleInputChange('idCardImage', null)}>{t('contract.wzRemove')}</button>
                          </div>
                        ) : (
                          <input type="file" className="form-control" accept="image/*,.pdf"
                            onChange={(e) => { const f = e.target.files[0]; if (f) { const r = new FileReader(); r.onload = () => handleInputChange('idCardImage', r.result); r.readAsDataURL(f); } }} />
                        )}
                      </div>
                      {needsWorkPermit() && (
                        <>
                          <div className="text-muted small mb-2">{t('contract.wzWorkPermitHint')}</div>
                          {/* Viele suchen ein eigenes Dokument „Arbeitserlaubnis" – die steht aber als
                              Satz auf dem Aufenthaltstitel. Erklärung VOR dem Upload, aufklappbar. */}
                          <details className="mb-3 border rounded p-2 bg-light" open>
                            <summary className="fw-bold" style={{ cursor: 'pointer' }}>
                              <i className="bi bi-info-circle me-1 text-primary"></i>{t('contract.wzWorkPermitHelpTitle')}
                            </summary>
                            <div className="small mt-2">{t('contract.wzWorkPermitHelp')}</div>
                          </details>
                          <div className="mb-3">
                            <label className="form-label fw-bold">{t('contract.wzWorkPermit')} *</label>
                            {formData.workPermitDocument ? (
                              <div className="d-flex align-items-center gap-2">
                                <DocView src={formData.workPermitDocument} alt="Arbeitserlaubnis" maxHeight={90} />
                                <button type="button" className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleInputChange('workPermitDocument', null)}>{t('contract.wzRemove')}</button>
                              </div>
                            ) : (
                              <input type="file" className="form-control" accept="image/*,.pdf"
                                onChange={(e) => { const f = e.target.files[0]; if (f) { const r = new FileReader(); r.onload = () => handleInputChange('workPermitDocument', r.result); r.readAsDataURL(f); } }} />
                            )}
                          </div>
                          <div className="mb-3">
                            <label className="form-label fw-bold">{t('contract.wzResidence')} *</label>
                            {formData.residencePermitDocument ? (
                              <div className="d-flex align-items-center gap-2">
                                <DocView src={formData.residencePermitDocument} alt="Aufenthaltstitel" maxHeight={90} />
                                <button type="button" className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleInputChange('residencePermitDocument', null)}>{t('contract.wzRemove')}</button>
                              </div>
                            ) : (
                              <input type="file" className="form-control" accept="image/*,.pdf"
                                onChange={(e) => { const f = e.target.files[0]; if (f) { const r = new FileReader(); r.onload = () => handleInputChange('residencePermitDocument', r.result); r.readAsDataURL(f); } }} />
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  <div className="d-flex justify-content-between mt-4">
                    {currentStep > 1 ? (
                      <button type="button" className="btn btn-outline-secondary" onClick={() => setCurrentStep(s => s - 1)}>
                        ← {t('contract.back')}
                      </button>
                    ) : <span />}
                    {currentStep < TOTAL_STEPS && (
                      <button type="button" className="btn btn-primary" onClick={handleNext}>
                        {t('contract.next')} →
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* ===== Schritt 4 / Nicht-Wizard: vollständiger Vertrag ===== */}
              {(!inWizard || currentStep === 4) && (<>

              <div className="text-center mb-4">
                <h4 className="fw-bold">Vertrag über ein kurzfristiges Beschäftigungsverhältnis</h4>
              </div>

              {/* Vertragspartner - Untereinander */}
              <div className="mb-4">
                <div className="mb-4">
                  <p><strong>Zwischen</strong></p>
                  <div className="p-3 bg-light rounded mb-3">
                    <p className="mb-1 fw-bold">{formData.employerName}</p>
                    <div style={{ whiteSpace: 'pre-line' }}>{formData.employerAddress}</div>
                  </div>
                  <p className="text-center">- nachfolgend „Arbeitgeber" genannt -</p>
                  <p className="mt-3"><strong>und</strong></p>
                </div>

                <div className="mb-4">
                  <p><strong>{t('contract.employeeLabel')}</strong></p>
                  
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group mb-2">
                        <label className="form-label fw-bold">{t('contract.nameLabel')}</label>
                        {showPersonalInputs ? (
                          <input
                            type="text"
                            className="form-control"
                            id="cf-employeeName" value={formData.employeeName}
                            onChange={(e) => handleInputChange('employeeName', e.target.value)}
                            placeholder={t('contract.namePlaceholder')}
                          />
                        ) : (
                          <p className="form-control-plaintext">{formData.employeeName || t('contract.toBeFilled')}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-2">
                    <label className="form-label fw-bold">{t('contract.address')}</label>
                    {showPersonalInputs ? (
                      <>
                        <div className="row g-2 mb-2">
                          <div className="col-8 col-md-6">
                            <input
                              type="text"
                              className="form-control"
                              id="cf-employeeStreet" value={formData.employeeStreet || ''}
                              onChange={(e) => handleInputChange('employeeStreet', e.target.value)}
                              placeholder={t('contract.streetPlaceholder')}
                            />
                            <small className="text-muted">{t('contract.street')}</small>
                          </div>
                          <div className="col-4 col-md-2">
                            <input
                              type="text"
                              className="form-control"
                              value={formData.employeeHouseNumber || ''}
                              onChange={(e) => handleInputChange('employeeHouseNumber', e.target.value)}
                              placeholder={t('contract.houseNrPlaceholder')}
                            />
                            <small className="text-muted">{t('contract.houseNr')}</small>
                          </div>
                        </div>
                        <div className="row g-2">
                          <div className="col-4 col-md-2">
                            <input
                              type="text"
                              className="form-control"
                              id="cf-employeePLZ" value={formData.employeePLZ || ''}
                              onChange={(e) => handleInputChange('employeePLZ', e.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
                              placeholder={t('contract.plzPlaceholder')}
                              maxLength={5}
                            />
                            <small className="text-muted">{t('contract.plz')}</small>
                          </div>
                          <div className="col-8 col-md-4">
                            <input
                              type="text"
                              className="form-control"
                              id="cf-employeeCity" value={formData.employeeCity || ''}
                              onChange={(e) => handleInputChange('employeeCity', e.target.value)}
                              placeholder={t('contract.cityPlaceholder')}
                            />
                            <small className="text-muted">{t('contract.city')}</small>
                          </div>
                        </div>
                      </>
                    ) : (
                      <p className="form-control-plaintext">{formData.employeeAddress || t('contract.toBeFilled')}</p>
                    )}
                  </div>
                  
                  <div className="row">
                    <div className="col-md-3">
                      <div className="form-group mb-2">
                        <label className="form-label fw-bold">{t('contract.birthdate')}</label>
                        {showPersonalInputs ? (
                          <input
                            type="date"
                            className="form-control"
                            id="cf-employeeBirthdate" value={formData.employeeBirthdate}
                            onChange={(e) => handleInputChange('employeeBirthdate', e.target.value)}
                          />
                        ) : (
                          <p className="form-control-plaintext">{fmtBirthDE(formData.employeeBirthdate) || t('contract.toBeFilled')}</p>
                        )}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group mb-2">
                        <label className="form-label fw-bold">{t('contract.nationality')}</label>
                        {showPersonalInputs ? (
                          <select
                            className="form-control"
                            id="cf-employeeNationality" value={formData.employeeNationality}
                            onChange={(e) => handleInputChange('employeeNationality', e.target.value)}
                          >
                            <option value="">{t('contract.pleaseSelect')}</option>
                            {euCountries.map((country) => (
                              <option key={country} value={country}>{country}</option>
                            ))}
                            <option value="Albanien">Albanien</option>
                            <option value="Afghanistan">Afghanistan</option>
                            <option value="Ägypten">Ägypten</option>
                            <option value="Algerien">Algerien</option>
                            <option value="Argentinien">Argentinien</option>
                            <option value="Armenien">Armenien</option>
                            <option value="Australien">Australien</option>
                            <option value="Aserbaidschan">Aserbaidschan</option>
                            <option value="Bangladesch">Bangladesch</option>
                            <option value="Belarus">Belarus</option>
                            <option value="Bosnien und Herzegowina">Bosnien und Herzegowina</option>
                            <option value="Brasilien">Brasilien</option>
                            <option value="China">China</option>
                            <option value="Georgien">Georgien</option>
                            <option value="Indien">Indien</option>
                            <option value="Indonesien">Indonesien</option>
                            <option value="Iran">Iran</option>
                            <option value="Irak">Irak</option>
                            <option value="Japan">Japan</option>
                            <option value="Kanada">Kanada</option>
                            <option value="Kasachstan">Kasachstan</option>
                            <option value="Kenia">Kenia</option>
                            <option value="Kolumbien">Kolumbien</option>
                            <option value="Kosovo">Kosovo</option>
                            <option value="Libanon">Libanon</option>
                            <option value="Marokko">Marokko</option>
                            <option value="Mexiko">Mexiko</option>
                            <option value="Moldau">Moldau</option>
                            <option value="Montenegro">Montenegro</option>
                            <option value="Nepal">Nepal</option>
                            <option value="Nigeria">Nigeria</option>
                            <option value="Nordmazedonien">Nordmazedonien</option>
                            <option value="Norwegen">Norwegen</option>
                            <option value="Pakistan">Pakistan</option>
                            <option value="Peru">Peru</option>
                            <option value="Philippinen">Philippinen</option>
                            <option value="Russland">Russland</option>
                            <option value="Schweiz">Schweiz</option>
                            <option value="Serbien">Serbien</option>
                            <option value="Südafrika">Südafrika</option>
                            <option value="Südkorea">Südkorea</option>
                            <option value="Sri Lanka">Sri Lanka</option>
                            <option value="Syrien">Syrien</option>
                            <option value="Thailand">Thailand</option>
                            <option value="Tunesien">Tunesien</option>
                            <option value="Türkei">Türkei</option>
                            <option value="Ukraine">Ukraine</option>
                            <option value="USA">USA</option>
                            <option value="Venezuela">Venezuela</option>
                            <option value="Vietnam">Vietnam</option>
                            <option value="Sonstige">Sonstige</option>
                          </select>
                        ) : (
                          <p className="form-control-plaintext">
                            {formData.employeeNationality || t('contract.toBeFilled')}
                            {formData.employeeNationality && !isEUCountry(formData.employeeNationality) && (
                              <span className="text-warning ms-2">
                                <i className="bi bi-exclamation-triangle"></i> {t('contract.workPermitRequired')}
                              </span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group mb-2">
                        <label className="form-label fw-bold">{t('contract.socialSecNr')}</label>
                        {showPersonalInputs ? (
                          <input
                            type="text"
                            className="form-control"
                            id="cf-socialSecurityNumber" value={formData.socialSecurityNumber}
                            onChange={(e) => handleInputChange('socialSecurityNumber', e.target.value)}
                            placeholder={t('contract.socialSecPlaceholder')}
                          />
                        ) : (
                          <p className="form-control-plaintext">{formData.socialSecurityNumber || t('contract.toBeFilled')}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group mb-2">
                        <label className="form-label fw-bold">{t('contract.birthPlace')}</label>
                        {showPersonalInputs ? (
                          <input
                            type="text"
                            className="form-control"
                            id="cf-birthPlace" value={formData.birthPlace}
                            onChange={(e) => handleInputChange('birthPlace', e.target.value)}
                            placeholder={t('contract.birthPlacePlaceholder')}
                          />
                        ) : (
                          <p className="form-control-plaintext">{formData.birthPlace || t('contract.toBeFilled')}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group mb-2">
                        <label className="form-label fw-bold">{t('contract.taxId')}</label>
                        {showPersonalInputs ? (
                          <input
                            type="text"
                            className="form-control"
                            id="cf-taxId" value={formData.taxId}
                            onChange={(e) => handleInputChange('taxId', e.target.value)}
                            placeholder={t('contract.taxIdPlaceholder')}
                          />
                        ) : (
                          <p className="form-control-plaintext">{formData.taxId || t('contract.toBeFilled')}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label className="form-label fw-bold">{t('contract.socialBenefits')} <span className="text-danger">*</span></label>
                        {showPersonalInputs ? (
                          <div>
                            {!formData.socialBenefitStatus && (
                              <small className="text-danger d-block mb-1">{t('contract.selectOption')}</small>
                            )}
                            {[
                              { value: 'grundsicherung', label: t('contract.basicSecurity') },
                              { value: 'arbeitslosengeld', label: t('contract.unemployment') },
                              { value: 'rente', label: t('contract.pension') },
                              { value: 'keine', label: t('contract.noBenefits') }
                            ].map(option => (
                              <div className="form-check" key={option.value}>
                                <input
                                  type="radio"
                                  className="form-check-input"
                                  id={`socialBenefit_${option.value}`}
                                  name="socialBenefitStatus"
                                  value={option.value}
                                  checked={formData.socialBenefitStatus === option.value}
                                  onChange={(e) => handleInputChange('socialBenefitStatus', e.target.value)}
                                />
                                <label className="form-check-label" htmlFor={`socialBenefit_${option.value}`}>
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="form-control-plaintext">
                            {formData.socialBenefitStatus === 'grundsicherung' ? t('contract.receivesBasicSecurity') :
                             formData.socialBenefitStatus === 'arbeitslosengeld' ? t('contract.receivesUnemployment') :
                             formData.socialBenefitStatus === 'rente' ? t('contract.receivesPension') : 
                             formData.socialBenefitStatus === 'keine' ? t('contract.receivesNone') : 
                             <span className="text-danger">{t('contract.notFilled')}</span>}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Pflicht: Ausweis-Upload (Vorderseite) */}
                  <div className="row">
                    <div className="col-md-8">
                      <div className="form-group mb-3">
                        <label className="form-label fw-bold">
                          {t('contract.idCard')} <span className="text-danger">*</span>
                        </label>
                        {showPersonalInputs ? (
                          <div>
                            {formData.idCardImage ? (
                              <div className="mb-2">
                                <div className="alert alert-success py-2 d-flex align-items-center justify-content-between">
                                  <span><i className="bi bi-check-circle"></i> {t('contract.idUploaded')}</span>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleInputChange('idCardImage', null)}
                                  >
                                    <i className="bi bi-trash"></i> {t('contract.remove')}
                                  </button>
                                </div>
                                {renderIdDoc(formData.idCardImage)}
                              </div>
                            ) : (
                              <div>
                                <input
                                  type="file"
                                  className="form-control"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      if (file.size > 10 * 1024 * 1024) {
                                        notify(t('contract.fileTooLarge'));
                                        return;
                                      }
                                      const reader = new FileReader();
                                      reader.onloadend = () => {
                                        handleInputChange('idCardImage', reader.result);
                                      };
                                      reader.readAsDataURL(file);
                                    }
                                  }}
                                />
                                <small className="text-muted">{t('contract.idUploadHelper')}</small>
                              </div>
                            )}
                          </div>
                        ) : (
                          formData.idCardImage ? (
                            renderIdDoc(formData.idCardImage)
                          ) : (
                            <p className="form-control-plaintext text-danger">{t('contract.notUploaded')}</p>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group mb-2">
                        <label className="form-label fw-bold">{t('contract.iban')}</label>
                        {showPersonalInputs ? (
                          <input
                            type="text"
                            className="form-control font-monospace"
                            id="cf-iban" value={formData.iban}
                            onChange={(e) => handleInputChange('iban', formatIBAN(e.target.value))}
                            placeholder={t('contract.ibanPlaceholder')}
                            maxLength={34}
                            style={{ letterSpacing: '0.05em' }}
                          />
                        ) : (
                          <p className="form-control-plaintext font-monospace">{formData.iban ? formatIBAN(formData.iban) : t('contract.toBeFilled')}</p>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-2">
                        <label className="form-label fw-bold">{t('contract.altAccountHolder')}</label>
                        {showPersonalInputs ? (
                          <input
                            type="text"
                            className="form-control"
                            value={formData.accountHolder || ''}
                            onChange={(e) => handleInputChange('accountHolder', e.target.value)}
                            placeholder={t('contract.accountHolderPlaceholder')}
                          />
                        ) : (
                          <p className="form-control-plaintext">{formData.accountHolder || '-'}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Pflicht-Upload für Nicht-EU-Bürger: Aufenthaltstitel/Arbeitserlaubnis */}
                  {needsWorkPermit() && (
                    <div className="row mt-3">
                      <div className="col-md-8">
                        <div className="form-group mb-3">
                          <label className="form-label fw-bold">
                            {t('contract.wzWorkPermit')} <span className="text-danger">*</span>
                          </label>
                          <div className="alert alert-warning py-2 mb-2">
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            {t('contract.nonEuNotice')}
                          </div>
                          {showPersonalInputs ? (
                            <div>
                              {formData.workPermitDocument ? (
                                <div className="mb-2">
                                  <div className="alert alert-success py-2 d-flex align-items-center justify-content-between">
                                    <span><i className="bi bi-check-circle"></i> {t('contract.docUploaded')}</span>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleInputChange('workPermitDocument', null)}
                                    >
                                      <i className="bi bi-trash"></i> {t('contract.remove')}
                                    </button>
                                  </div>
                                  <DocView src={formData.workPermitDocument} alt="Arbeitserlaubnis" />
                                </div>
                              ) : (
                                <div>
                                  <input
                                    type="file"
                                    className="form-control"
                                    accept="image/*,.pdf"
                                    onChange={(e) => {
                                      const file = e.target.files[0];
                                      if (file) {
                                        if (file.size > 10 * 1024 * 1024) {
                                          notify(t('contract.fileTooLarge'));
                                          return;
                                        }
                                        const reader = new FileReader();
                                        reader.onloadend = () => {
                                          handleInputChange('workPermitDocument', reader.result);
                                        };
                                        reader.readAsDataURL(file);
                                      }
                                    }}
                                  />
                                  <small className="text-muted">{t('contract.workPermitUploadHelper')}</small>
                                </div>
                              )}
                            </div>
                          ) : (
                            formData.workPermitDocument ? (
                              <DocView src={formData.workPermitDocument} alt="Arbeitserlaubnis" />
                            ) : (
                              <p className="form-control-plaintext text-danger">{t('contract.notUploaded')}</p>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Aufenthaltstitel (separates Dokument, Nicht-EU) */}
                  {needsWorkPermit() && (
                    <div className="row mt-3">
                      <div className="col-md-8">
                        <div className="form-group mb-3">
                          <label className="form-label fw-bold">
                            {t('contract.wzResidence')} <span className="text-danger">*</span>
                          </label>
                          {showPersonalInputs ? (
                            formData.residencePermitDocument ? (
                              <div className="alert alert-success py-2 d-flex align-items-center justify-content-between">
                                <span><i className="bi bi-check-circle"></i> {t('contract.docUploaded')}</span>
                                <button type="button" className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleInputChange('residencePermitDocument', null)}>
                                  <i className="bi bi-trash"></i> {t('contract.remove')}
                                </button>
                              </div>
                            ) : (
                              <input type="file" className="form-control" accept="image/*,.pdf"
                                onChange={(e) => { const f = e.target.files[0]; if (f) { const r = new FileReader(); r.onloadend = () => handleInputChange('residencePermitDocument', r.result); r.readAsDataURL(f); } }} />
                            )
                          ) : (
                            formData.residencePermitDocument ? (
                              <DocView src={formData.residencePermitDocument} alt="Aufenthaltstitel" />
                            ) : (
                              <p className="form-control-plaintext text-danger">{t('contract.notUploaded')}</p>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <p className="text-center mt-3">nachfolgend „Arbeitnehmer/-in" genannt - wird Folgendes geschlossen:</p>
                </div>
              </div>

              {/* § 1 Beginn des Arbeitsverhältnisses/Tätigkeit */}
              <div className="mb-4">
                <h5 className="fw-bold">§ 1 Beginn des Arbeitsverhältnisses/Tätigkeit</h5>
                <p>Der Arbeitnehmer wird mit Wirkung ab dem 
                {(isEditing && !isEmployee) ? (
                  <input
                    type="date"
                    className="form-control d-inline"
                    style={{ width: 'auto', display: 'inline-block', margin: '0 5px' }}
                    value={formatDateForInput(formData.startDate)}
                    onChange={(e) => handleDateChange('startDate', e.target.value)}
                  />
                ) : (
                  <span className="mx-2 fw-bold">{formatDateForDisplay(formData.startDate)}</span>
                )}
                als {(isEditing && !isEmployee) ? (
                  <textarea
                    className="form-control d-inline"
                    style={{ width: '100%', marginTop: '10px' }}
                    rows="2"
                    value={formData.position}
                    onChange={(e) => handleInputChange('position', e.target.value)}
                  />
                ) : (
                  <span className="fw-bold">{formData.position}</span>
                )} eingestellt.</p>
              </div>

              {/* § 2 Beendigung des Arbeitsverhältnisses */}
              <div className="mb-4">
                <h5 className="fw-bold">§ 2 Beendigung des Arbeitsverhältnisses</h5>
                <p>Das Arbeitsverhältnis endet mit Ablauf des 
                {(isEditing && !isEmployee) ? (
                  <input
                    type="date"
                    className="form-control d-inline"
                    style={{ width: 'auto', display: 'inline-block', margin: '0 5px' }}
                    value={formatDateForInput(formData.endDate)}
                    onChange={(e) => handleDateChange('endDate', e.target.value)}
                  />
                ) : (
                  <span className="mx-2 fw-bold">{formatDateForDisplay(formData.endDate)}</span>
                )} ohne dass es einer ausdrücklichen Kündigung bedarf.</p>
              </div>

              {/* § 3 Arbeitszeit */}
              <div className="mb-4">
                <h5 className="fw-bold">§ 3 Arbeitszeit</h5>
                <p>Die Arbeitszeit beträgt 
                {(isEditing && !isEmployee) ? (
                  <input
                    type="text"
                    className="form-control d-inline"
                    style={{ width: 'auto', display: 'inline-block', margin: '0 5px' }}
                    value={formData.workingTime}
                    onChange={(e) => handleInputChange('workingTime', e.target.value)}
                  />
                ) : (
                  <span className="mx-2 fw-bold">{formData.workingTime}</span>
                )}. Sie kann sich jedoch nach Bedarf noch verändern und wird anhand der tatsächlich gearbeiteten und nachgewiesenen Stunden abgerechnet.</p>
              </div>

              {/* § 4 Pausen */}
              <div className="mb-4">
                <h5 className="fw-bold">§ 4 Pausen</h5>
                <p>{(isEditing && !isEmployee) ? (
                  <textarea
                    className="form-control"
                    rows="2"
                    value={formData.breakTime}
                    onChange={(e) => handleInputChange('breakTime', e.target.value)}
                  />
                ) : (
                  formData.breakTime
                )}</p>
              </div>

              {/* § 5 Urlaub */}
              <div className="mb-4">
                <h5 className="fw-bold">§ 5 Urlaub</h5>
                <p>{(isEditing && !isEmployee) ? (
                  <textarea
                    className="form-control"
                    rows="2"
                    value={formData.vacationDays}
                    onChange={(e) => handleInputChange('vacationDays', e.target.value)}
                  />
                ) : (
                  formData.vacationDays
                )}</p>
              </div>

              {/* § 6 Vergütung */}
              <div className="mb-4">
                <h5 className="fw-bold">§ 6 Vergütung</h5>
                <p>Der Arbeitnehmer erhält eine Vergütung von 
                {(isEditing && !isEmployee) ? (
                  <input
                    type="number"
                    step="0.01"
                    className="form-control d-inline"
                    style={{ width: '80px', display: 'inline-block', margin: '0 5px' }}
                    value={formData.salary}
                    onChange={(e) => handleInputChange('salary', e.target.value)}
                  />
                ) : (
                  <span className="mx-2 fw-bold">{formData.salary}</span>
                )} Euro je Stunde und wird nach Ende des Oktoberfests auf ein dem Arbeitgeber zu nennendes Konto ausbezahlt.</p>
              </div>

              {/* § 7 Verschwiegenheitspflicht */}
              <div className="mb-4">
                <h5 className="fw-bold">§ 7 Verschwiegenheitspflicht</h5>
                <p>{(isEditing && !isEmployee) ? (
                  <textarea
                    className="form-control"
                    rows="3"
                    value={formData.specialAgreements}
                    onChange={(e) => handleInputChange('specialAgreements', e.target.value)}
                  />
                ) : (
                  formData.specialAgreements
                )}</p>
              </div>

              {/* § 8 Status der kurzfristigen Beschäftigung */}
              <div className="mb-4">
                <h5 className="fw-bold">§ 8 Status der kurzfristigen Beschäftigung</h5>
                <p>{(isEditing && !isEmployee) ? (
                  <textarea
                    className="form-control"
                    rows="4"
                    value={formData.kurzfristigeDetails}
                    onChange={(e) => handleInputChange('kurzfristigeDetails', e.target.value)}
                  />
                ) : (
                  formData.kurzfristigeDetails
                )}</p>

                <div className="mt-3">
                  <p><strong>{t('contract.prevJobs')}</strong></p>
                  {formData.noPreviousJobs ? (
                    !showPersonalInputs ? (
                      <p>{t('contract.noPrevJobs')}</p>
                    ) : (
                      <div className="mt-2">
                        <label>
                          <input 
                            type="checkbox" 
                            className="me-2" 
                            checked={formData.noPreviousJobs || false}
                            onChange={(e) => setNoPreviousJobs(e.target.checked)}
                            disabled={!isEditing}
                          />
                          {t('contract.noPrevJobsCheck')}
                        </label>
                      </div>
                    )
                  ) : (
                    <>
                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead className="table-light">
                        <tr>
                          <th>{t('contract.from')}</th>
                          <th>{t('contract.to')}</th>
                          <th>{t('contract.totalDays')}</th>
                          <th>{t('contract.employer')}</th>
                          {isEditing && <th>{t('contract.actions')}</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {formData.previousJobs.map((job, index) => (
                          <tr key={index}>
                            <td>
                              {showPersonalInputs ? (
                                <input
                                  type="date"
                                  className="form-control"
                                  value={formatDateForInput(job.from)}
                                  onChange={(e) => updatePreviousJob(index, 'from', new Date(e.target.value).toLocaleDateString('de-DE'))}
                                />
                              ) : (
                                formatDateForDisplay(job.from)
                              )}
                            </td>
                            <td>
                              {showPersonalInputs ? (
                                <input
                                  type="date"
                                  className="form-control"
                                  value={formatDateForInput(job.to)}
                                  onChange={(e) => updatePreviousJob(index, 'to', new Date(e.target.value).toLocaleDateString('de-DE'))}
                                />
                              ) : (
                                formatDateForDisplay(job.to)
                              )}
                            </td>
                            <td>
                              {showPersonalInputs ? (
                                <input
                                  type="number"
                                  className="form-control"
                                  value={job.totalDays}
                                  onChange={(e) => updatePreviousJob(index, 'totalDays', e.target.value)}
                                  placeholder={t('contract.daysPlaceholder')}
                                />
                              ) : (
                                job.totalDays || '-'
                              )}
                            </td>
                            <td>
                              {showPersonalInputs ? (
                                <input
                                  type="text"
                                  className="form-control"
                                  value={job.employer}
                                  onChange={(e) => updatePreviousJob(index, 'employer', e.target.value)}
                                  placeholder={t('contract.companyPlaceholder')}
                                />
                              ) : (
                                job.employer || '-'
                              )}
                            </td>
                            {isEditing && (
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => removePreviousJob(index)}
                                  disabled={formData.previousJobs.length === 1}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {isEditing && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={addPreviousJob}
                    >
                      <i className="bi bi-plus me-1"></i>
                      {t('contract.addJob')}
                    </button>
                  )}
                  <div className="mt-2">
                    <label>
                      <input 
                        type="checkbox" 
                        className="me-2" 
                        checked={formData.noPreviousJobs || false}
                        onChange={(e) => setNoPreviousJobs(e.target.checked)}
                        disabled={!isEditing}
                      />
                      {t('contract.noPrevJobsCheck')}
                    </label>
                  </div>
                    </>
                  )}
                </div>
              </div>

              {/* § 9 Verfall-/Ausschlussfristen */}
              <div className="mb-4">
                <h5 className="fw-bold">§ 9 Verfall-/Ausschlussfristen</h5>
                <p>{(isEditing && !isEmployee) ? (
                  <textarea
                    className="form-control"
                    rows="4"
                    value={formData.verfallsfristen}
                    onChange={(e) => handleInputChange('verfallsfristen', e.target.value)}
                  />
                ) : (
                  formData.verfallsfristen
                )}</p>
              </div>

              {/* § 10 Vertragsänderungen und Nebenabreden */}
              <div className="mb-4">
                <h5 className="fw-bold">§ 10 Vertragsänderungen und Nebenabreden</h5>
                <p>{(isEditing && !isEmployee) ? (
                  <textarea
                    className="form-control"
                    rows="3"
                    value={formData.vertragsaenderungen}
                    onChange={(e) => handleInputChange('vertragsaenderungen', e.target.value)}
                  />
                ) : (
                  formData.vertragsaenderungen
                )}</p>
              </div>

              {/* Unterschriften */}
              <div className="mb-4">
                <div className="row mt-5">
                  <div className="col-md-12">
                    <div className="border p-2 mb-2" style={{ minHeight: '50px' }}>
                      <label className="small">{t('contract.placeDate')}</label>
                      <div className="d-flex align-items-center gap-2">
                        {(isEditing || isEmployee || user.role === 'admin') ? (
                          <input
                            type="text"
                            className={`form-control form-control-sm d-inline ${String(formData.signatureCity || '').trim() ? '' : 'border-danger'}`}
                            style={{ width: '200px' }}
                            id="cf-signatureCity" value={formData.signatureCity || ''}
                            onChange={(e) => handleInputChange('signatureCity', e.target.value)}
                            placeholder={t('contract.placePlaceholder')}
                          />
                        ) : (
                          <span>{formData.signatureCity || t('contract.placeDefault')}</span>
                        )}
                        <span>, {new Date().toLocaleDateString('de-DE')}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row mt-4">
                  <div className="col-md-6">
                    <div className="border p-2 mb-2" style={{ minHeight: '120px', position: 'relative' }}>
                      {formData.employeeSignature ? (
                        <div>
                          <img 
                            src={formData.employeeSignature} 
                            alt="Unterschrift Arbeitnehmer" 
                            style={{ maxWidth: '100%', maxHeight: '100px' }} 
                          />
                          <div className="small text-muted">
                            {t('contract.signedOn')} {formData.employeeSignatureDate}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-muted d-flex align-items-center justify-content-center h-100">
                          {t('contract.notSigned')}
                        </div>
                      )}
                    </div>
                    <p className="text-center mb-2"><strong>{t('contract.employeeSignature')}</strong></p>
                    
                    {(isEmployee || user.role === 'admin') && (
                      <div className="text-center">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => openSignatureModal('employee')}
                        >
                          <i className="bi bi-pencil"></i> {t('contract.signDigitally')}
                        </button>
                        {formData.employeeSignature && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger ms-2"
                            onClick={() => {
                              handleInputChange('employeeSignature', '');
                              handleInputChange('employeeSignatureDate', '');
                            }}
                          >
                            <i className="bi bi-trash"></i> {t('contract.deleteSignature')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="col-md-6">
                    <div className="border p-2 mb-2" style={{ minHeight: '120px', position: 'relative' }}>
                      {formData.employerSignature ? (
                        <div>
                          <img
                            src={formData.employerSignature}
                            alt="Unterschrift Arbeitgeber"
                            style={{ maxWidth: '100%', maxHeight: '100px' }}
                          />
                          <div className="small text-muted">
                            {t('contract.signedOn')} {formData.employerSignatureDate}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-muted d-flex align-items-center justify-content-center h-100">
                          {t('contract.employerSignatureNote')}
                        </div>
                      )}
                    </div>
                    <p className="text-center"><strong>{t('contract.employerSignature')}</strong></p>
                  </div>
                </div>

                {isMinor() && (
                <div className="row mt-4">
                  <div className="col-md-6">
                    <div className="border p-2 mb-2" style={{ minHeight: '120px', position: 'relative' }}>
                      {formData.parentSignature ? (
                        <div>
                          <img 
                            src={formData.parentSignature} 
                            alt="Unterschrift Erziehungsberechtigter" 
                            style={{ maxWidth: '100%', maxHeight: '100px' }} 
                          />
                          <div className="small text-muted">
                            {t('contract.signedOn')} {formData.parentSignatureDate}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-muted d-flex align-items-center justify-content-center h-100">
                          {t('contract.minorRequired')}
                        </div>
                      )}
                    </div>
                    <p className="text-center mb-2"><strong>{t('contract.parentSignature')}</strong><br />
                    <small className="text-muted">{t('contract.parentNote')}</small></p>
                    
                    {(isEmployee || user.role === 'admin') && (
                      <div className="text-center">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => openSignatureModal('parent')}
                        >
                          <i className="bi bi-pencil"></i> {t('contract.signDigitally')}
                        </button>
                        {formData.parentSignature && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger ms-2"
                            onClick={() => {
                              handleInputChange('parentSignature', '');
                              handleInputChange('parentSignatureDate', '');
                            }}
                          >
                            <i className="bi bi-trash"></i> {t('contract.deleteSignature')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                )}
              </div>

              {/* Status und Metadaten */}
              {user.role === 'admin' && (() => {
                // Interne Infos: nur für Admins sichtbar, werden NICHT ins PDF gedruckt (no-pdf).
                // Datumswerte liegen als "1.9.2026" vor – new Date() macht daraus "Invalid Date".
                const fmt = (v) => {
                  if (!v) return '—';
                  const s = String(v);
                  if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(s)) return s;
                  const d = new Date(s);
                  return isNaN(d.getTime()) ? s : d.toLocaleString('de-DE');
                };
                const statusDe = { draft: 'Entwurf', submitted: 'eingereicht', approved: 'genehmigt', rejected: 'abgelehnt' }[formData.status] || formData.status || '—';
                return (
                  <div className="alert alert-info no-pdf">
                    <h6>{t('contract.adminInfo')}</h6>
                    <div className="row">
                      <div className="col-md-4">
                        <strong>{t('contract.statusLabel')}</strong> {statusDe}
                      </div>
                      <div className="col-md-4">
                        <strong>{t('contract.created')}</strong> {fmt(formData.createdDate)}
                      </div>
                      <div className="col-md-4">
                        <strong>{t('contract.modified')}</strong> {fmt(formData.lastModified)}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Submit Buttons for Employees - Bottom of Form */}
              {isEmployee && !readonly && isEditing && (
                <div className="row">
                  <div className="col-12">
                    <div className="card mt-4">
                      <div className="card-body text-center">
                        <h5 className="card-title">
                          <i className="bi bi-check-circle me-2"></i>
                          {t('contract.finalize')}
                        </h5>
                        <p className="card-text text-muted">
                          {t('contract.finalizeHint')}
                        </p>

                        {/* PDF Language Notice */}
                        {contractPdfLang === 'de' ? (
                          <div className="alert alert-info py-2 mb-3">
                            <i className="bi bi-info-circle me-2"></i>
                            {t('contract.pdfLanguageNotice')}
                          </div>
                        ) : (
                          <div className="alert alert-warning py-2 mb-3">
                            <i className="bi bi-translate me-2"></i>
                            {t('contract.pdfTranslationNotice')}
                          </div>
                        )}

                        {/* Template Options Modal */}
                        {showTemplateOptions && (
                          <div className="alert alert-light border border-primary mb-3">
                            <h6 className="alert-heading">
                              <i className="bi bi-file-earmark-text me-2"></i>
                              {t('contract.templateOptions')}
                            </h6>
                            <p className="mb-3">
                              {t('contract.templateOptionsHint')}
                            </p>
                            
                            <div className="form-check mb-2">
                              <input 
                                className="form-check-input" 
                                type="radio" 
                                name="templateOption" 
                                id="noTemplate"
                                checked={!saveAsTemplate && !deleteTemplate}
                                onChange={() => {
                                  setSaveAsTemplate(false);
                                  setDeleteTemplate(false);
                                }}
                              />
                              <label className="form-check-label fw-bold" htmlFor="noTemplate">
                                <i className="bi bi-x-circle me-1 text-secondary"></i>
                                <strong>{t('contract.noTemplate')}</strong> {t('contract.default')}
                              </label>
                            </div>

                            <div className="form-check mb-2">
                              <input 
                                className="form-check-input" 
                                type="radio" 
                                name="templateOption" 
                                id="saveTemplate"
                                checked={saveAsTemplate && !deleteTemplate}
                                onChange={() => {
                                  setSaveAsTemplate(true);
                                  setDeleteTemplate(false);
                                }}
                              />
                              <label className="form-check-label" htmlFor="saveTemplate">
                                <i className="bi bi-save me-1 text-success"></i>
                                {t('contract.yesTemplate')}
                              </label>
                            </div>

                            {hasExistingTemplate && (
                              <div className="form-check mb-3">
                                <input 
                                  className="form-check-input" 
                                  type="radio" 
                                  name="templateOption" 
                                  id="deleteTemplate"
                                  checked={deleteTemplate && !saveAsTemplate}
                                  onChange={() => {
                                    setSaveAsTemplate(false);
                                    setDeleteTemplate(true);
                                  }}
                                />
                                <label className="form-check-label" htmlFor="deleteTemplate">
                                  <i className="bi bi-trash me-1 text-danger"></i>
                                  {t('contract.deleteTemplateOption')}
                                </label>
                              </div>
                            )}

                            <hr className="my-3" />
                            
                            <div className="d-flex justify-content-center gap-3">
                              <button 
                                className="btn btn-success btn-lg px-4" 
                                onClick={() => {
                                  const templateOptions = { saveAsTemplate, deleteTemplate };
                                  handleSave(false, templateOptions);
                                }}
                                disabled={isSaving || isSubmitting}
                              >
                                {isSaving || isSubmitting ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {t('contract.submitting')}
                                  </>
                                ) : (
                                  <>
                                    <i className="bi bi-check-circle me-2"></i>
                                    {t('contract.submit')}
                                  </>
                                )}
                              </button>
                              <button 
                                className="btn btn-outline-secondary btn-lg px-4" 
                                onClick={() => setShowTemplateOptions(false)}
                                disabled={isSaving || isSubmitting}
                              >
                                <i className="bi bi-arrow-left me-2"></i>
                                {t('contract.back')}
                              </button>
                            </div>
                          </div>
                        )}

                        {!showTemplateOptions && (
                        <div>
                          {/* Nach dem Unterschreiben halten viele den Vertrag für erledigt –
                              der Knopf allein reicht als Signal nicht. Deshalb hier ein
                              unübersehbarer Hinweis, solange noch nicht eingereicht ist. */}
                          {isEmployee && formData.employeeSignature
                            && !['submitted', 'approved'].includes(formData.status) && (
                            <div className="alert alert-warning d-flex align-items-center gap-2 mb-3"
                              style={{ borderLeftWidth: '6px' }}>
                              <i className="bi bi-exclamation-triangle-fill fs-4"></i>
                              <div className="text-start">
                                <strong>Fast geschafft – aber noch nicht abgeschickt!</strong>
                                <div className="small">
                                  Deine Unterschrift steht drin, wir sehen den Vertrag aber erst,
                                  wenn du unten auf <strong>„{t('contract.submit')}"</strong> tippst.
                                </div>
                              </div>
                            </div>
                          )}
                          <button
                            className="btn btn-primary btn-lg me-3"
                            onClick={handleSubmit}
                            disabled={isSaving || isSubmitting}
                          >
                            {isSaving || isSubmitting ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {t('contract.submitting')}
                              </>
                            ) : (
                              <>
                                <i className="bi bi-check-circle me-2"></i>
                                {t('contract.submit')}
                              </>
                            )}
                          </button>
                          <button 
                            className="btn btn-success me-3" 
                            onClick={() => handleSave(true)}
                            disabled={isSaving || isSubmitting}
                          >
                            <i className="bi bi-save me-2"></i>
                            {isSaving ? t('contract.saving') : t('contract.saveDraft')}
                          </button>
                          <button 
                            className="btn btn-secondary" 
                            onClick={() => setIsEditing(false)}
                          >
                            <i className="bi bi-x-lg me-2"></i>
                            {t('contract.cancel')}
                          </button>
                        </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Ende Schritt-4-Gate (Dokument) */}
              </>)}
            </div>
          </div>

          {/* Contract History for Employees */}
          {isEmployee && (
            <div className="col-12 mt-4">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="bi bi-clock-history me-2"></i>
                    {t('contract.myContracts')}
                  </h5>
                  <button 
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setShowHistory(!showHistory)}
                  >
                    <i className={`bi ${showHistory ? 'bi-chevron-up' : 'bi-chevron-down'} me-1`}></i>
                    {showHistory ? t('contract.hide') : t('contract.show')}
                  </button>
                </div>
                
                {showHistory && (
                  <div className="card-body">
                    {isLoadingHistory ? (
                      <div className="text-center py-3">
                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                        {t('contract.loadingHistory')}
                      </div>
                    ) : contractHistory.length === 0 ? (
                      <div className="text-center text-muted py-3">
                        <i className="bi bi-inbox display-4 mb-3"></i>
                        <p>{t('contract.noContracts')}</p>
                        <small>{t('contract.contractsHelperText')}</small>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th>{t('contract.createdAt')}</th>
                              <th>{t('contract.position')}</th>
                              <th>{t('contract.period')}</th>
                              <th>{t('contract.statusLabel')}</th>
                              <th>{t('contract.submittedAt')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {contractHistory.map((contract, index) => (
                              <tr key={contract.id}>
                                <td>
                                  <div>
                                    <div>{contract.created_at ? new Date(contract.created_at).toLocaleDateString('de-DE') : 'N/A'}</div>
                                    <small className="text-muted">
                                      {contract.created_at ? new Date(contract.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : ''}
                                    </small>
                                  </div>
                                </td>
                                <td>
                                  <div className="fw-bold">{contract.position || t('contract.notSpecified')}</div>
                                  <small className="text-muted">Vertrag #{contract.id}</small>
                                </td>
                                <td>
                                  <div>
                                    {contract.startDate && contract.endDate ? (
                                      <span>
                                        {formatDateForDisplay(contract.startDate)} - {formatDateForDisplay(contract.endDate)}
                                      </span>
                                    ) : t('contract.notSpecified')}
                                  </div>
                                </td>
                                <td>
                                  <span className={`badge ${
                                    contract.status === 'submitted' ? 'bg-warning text-dark' :
                                    contract.status === 'approved' ? 'bg-success' :
                                    contract.status === 'rejected' ? 'bg-danger' : 
                                    contract.status === 'draft' ? 'bg-secondary' : 'bg-light text-dark'
                                  }`}>
                                    {contract.status === 'submitted' ? t('contract.statusSubmitted') :
                                     contract.status === 'approved' ? t('contract.statusApproved') :
                                     contract.status === 'rejected' ? t('contract.statusRejected') :
                                     contract.status === 'draft' ? t('contract.statusDraft') : contract.status}
                                  </span>
                                </td>
                                <td>
                                  {contract.submitted_at ? (
                                    <div>
                                      <div>{new Date(contract.submitted_at).toLocaleDateString('de-DE')}</div>
                                      <small className="text-muted">
                                        {new Date(contract.submitted_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                                      </small>
                                    </div>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="mt-3">
                          <small className="text-muted">
                            <i className="bi bi-info-circle me-1"></i>
                            Gesamt: {contractHistory.length} Vertrag{contractHistory.length !== 1 ? 'e' : ''} 
                            ({contractHistory.filter(c => c.status === 'submitted').length} eingereicht, 
                             {contractHistory.filter(c => c.status === 'draft').length} Entwürfe)
                          </small>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Signature Modal */}
      {showSignatureModal && (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050, overflow: 'auto' }}>
          <div className="modal-dialog modal-lg" style={{ margin: '1rem auto' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-pencil me-2"></i>
                  {t('contract.digitalSignature')} - {currentSignatureType === 'employee' ? t('contract.signatureEmployee') : t('contract.signatureParent')}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowSignatureModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label small fw-bold" htmlFor="sig-city">
                    {t('contract.placeDate')}
                  </label>
                  <div className="d-flex align-items-center gap-2">
                    <input
                      id="sig-city"
                      type="text"
                      className={`form-control ${String(formData.signatureCity || '').trim() ? '' : 'border-danger'}`}
                      style={{ maxWidth: '260px' }}
                      value={formData.signatureCity || ''}
                      onChange={(e) => handleInputChange('signatureCity', e.target.value)}
                      placeholder={t('contract.placePlaceholder')}
                    />
                    <span className="text-muted">, {new Date().toLocaleDateString('de-DE')}</span>
                  </div>
                </div>

                <div className="text-center mb-3">
                  <p>{t('contract.signatureHint')}</p>
                </div>
                
                <div className="border" style={{ display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
                  <canvas
                    ref={currentSignatureType === 'employee' ? employeeCanvasRef : parentCanvasRef}
                    width={500}
                    height={200}
                    style={{ border: '1px solid #ddd', cursor: 'crosshair', touchAction: 'none', maxWidth: '100%' }}
                    onMouseDown={(e) => startDrawing(e, 
                      currentSignatureType === 'employee' ? employeeCanvasRef : parentCanvasRef,
                      currentSignatureType === 'employee' ? setIsDrawingEmployee : setIsDrawingParent
                    )}
                    onMouseMove={(e) => draw(e, 
                      currentSignatureType === 'employee' ? employeeCanvasRef : parentCanvasRef,
                      currentSignatureType === 'employee' ? isDrawingEmployee : isDrawingParent
                    )}
                    onMouseUp={(e) => stopDrawing(e,
                      currentSignatureType === 'employee' ? setIsDrawingEmployee : setIsDrawingParent
                    )}
                    onMouseOut={(e) => stopDrawing(e,
                      currentSignatureType === 'employee' ? setIsDrawingEmployee : setIsDrawingParent
                    )}
                    onTouchStart={(e) => startDrawing(e, 
                      currentSignatureType === 'employee' ? employeeCanvasRef : parentCanvasRef,
                      currentSignatureType === 'employee' ? setIsDrawingEmployee : setIsDrawingParent
                    )}
                    onTouchMove={(e) => draw(e, 
                      currentSignatureType === 'employee' ? employeeCanvasRef : parentCanvasRef,
                      currentSignatureType === 'employee' ? isDrawingEmployee : isDrawingParent
                    )}
                    onTouchEnd={(e) => stopDrawing(e,
                      currentSignatureType === 'employee' ? setIsDrawingEmployee : setIsDrawingParent
                    )}
                  />
                </div>
                
                <div className="text-center mt-3">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary me-2"
                    onClick={() => clearCanvas(
                      currentSignatureType === 'employee' ? employeeCanvasRef : parentCanvasRef
                    )}
                  >
                    <i className="bi bi-arrow-clockwise"></i> {t('contract.restart')}
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowSignatureModal(false)}
                >
                  {t('contract.cancel')}
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  disabled={!String(formData.signatureCity || '').trim()}
                  title={String(formData.signatureCity || '').trim() ? '' : t('contract.reqPlace')}
                  onClick={saveSignature}
                >
                  <i className="bi bi-check"></i> {t('contract.acceptSignature')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default DigitalWorkContract;
